import { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  Box,
  Grid,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  Autocomplete,
  CircularProgress
} from '@mui/material';
import { Close, Add, Delete } from '@mui/icons-material';
import { fetchData, postData, updateData } from '../../../apiService';

const SaleReturnModal = ({
  open,
  onClose,
  onSave,
  companyId,
  branchId,
  token,
  customers = [],
  setLoading,
  setLoadingMessage,
  setError,
  setSuccess,
  fetchSaleReturns,
  setCreateModalOpen,
  userId,
  modalAction = 'create',
  selectedSaleReturn = null,
  saleReturnDetails = []
}) => {
  console.log('SaleReturnModal props:', { open, companyId, branchId, token: token ? 'exists' : 'missing', customersCount: customers.length });

  // Track initialization to prevent infinite loops
  const initializedRef = useRef(false);
  const prevOpenRef = useRef(open);
  const prevModalActionRef = useRef(modalAction);

  // Helper function to format date as dd-mm-yyyy
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const [formData, setFormData] = useState({
    billNo: '',
    billDate: new Date().toISOString().split('T')[0],
    SaleId: '',
    SaleNo: '',
    SaleBillPrefix: '',
    SaleBillNumber: '',
    SaleFinYear: '',
    customerId: '',
    customer: '',
    vatNo: '',
    invType: '',
    vatType: 'Excluding Tax',
    SalesManId: '',
    CurrencyTypeId: null,
    CurrencyName: ''
  });

  const [itemsList, setItemsList] = useState([]);
  const [Sales, setSales] = useState([]);
  const [banks, setBanks] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [items, setItems] = useState([]);
  const [viewAllItemsModalOpen, setViewAllItemsModalOpen] = useState(false);
  const [validationError, setValidationError] = useState('');

  // Currency dropdown state
  const [currencies, setCurrencies] = useState([]);
  const [currencyLoading, setCurrencyLoading] = useState(false);

  const [additionalCharges, setAdditionalCharges] = useState({
    freight: 0,
    packing: 0,
    tcsAmount: 0,
    cashAmount: 0,
    upiAmount: 0,
    cardAmount: 0,
    bankId: '',
    remarks: ''
  });

  const [summary, setSummary] = useState({
    subtotal: 0,
    discount: 0,
    taxable: 0,
    // cgst: 0,
    // sgst: 0,
    // igst: 0,
    vatamount: 0,
    igst: 0,
    freightVAT: 0,
    packingVAT: 0,
    effectiveVATRate: 5,
    tcs: 0,
    roundOff: 0,
    total: 0,
    paid: 0,
    balance: 0
  });

  // Check if invoice type is IGST
  // B to B and B to C use CGST+SGST, others use IGST
  const isIGSTInvoice = () => {
    const invoiceType = (formData.invType || '').toLowerCase();
    // If invoice type contains 'b to b' or 'b to c' or 'b2b' or 'b2c', use CGST+SGST
    if (invoiceType.includes('b to b') || invoiceType.includes('b to c') ||
      invoiceType.includes('b2b') || invoiceType.includes('b2c')) {
      return false; // Use CGST + SGST
    }
    // For IGST type or any other type, use IGST
    return invoiceType.includes('igst') || invoiceType.length > 0;
  };

  // Fetch currencies
  useEffect(() => {
    const fetchCurrencies = async () => {
      if (!companyId || !token || !open) return;
      setCurrencyLoading(true);
      try {
        const response = await fetchData('CurrencyTypeApi', `/${companyId}`, token);
        if (Array.isArray(response)) {
          setCurrencies(response);
        } else if (response?.response_data && Array.isArray(response.response_data)) {
          setCurrencies(response.response_data);
        } else {
          setCurrencies([]);
        }
      } catch (error) {
        console.error('Error fetching currencies:', error);
        setCurrencies([]);
      } finally {
        setCurrencyLoading(false);
      }
    };

    fetchCurrencies();
  }, [companyId, token, open]);

  // Get currency symbol from currency name
  const getCurrencySymbol = (currencyName) => {
    if (!currencyName) return 'د.إ';
    const lower = currencyName.toLowerCase().trim();
    const symbolMap = {
      'inr': '₹', 'rupee': '₹', 'indian rupee': '₹',
      'usd': '$', 'dollar': '$', 'us dollar': '$',
      'eur': '€', 'euro': '€',
      'gbp': '£', 'pound': '£', 'british pound': '£',
      'aed': 'د.إ', 'dirham': 'د.إ', 'uae dirham': 'د.إ',
      'sar': '﷼', 'riyal': '﷼', 'saudi riyal': '﷼',
      'jpy': '¥', 'yen': '¥',
      'cny': '¥', 'yuan': '¥', 'rmb': '¥',
      'aud': 'A$', 'australian dollar': 'A$',
      'cad': 'C$', 'canadian dollar': 'C$',
      'sgd': 'S$', 'singapore dollar': 'S$',
      'chf': 'CHF', 'swiss franc': 'CHF',
      'myr': 'RM', 'ringgit': 'RM',
      'bdt': '৳', 'taka': '৳',
      'pkr': '₨', 'pakistani rupee': '₨',
      'omr': 'OMR', 'omani rial': 'OMR',
      'qar': 'QR', 'qatari riyal': 'QR',
      'kwd': 'KD', 'kuwaiti dinar': 'KD',
      'bhd': 'BD', 'bahraini dinar': 'BD'
    };
    if (symbolMap[lower]) return symbolMap[lower];
    for (const [key, symbol] of Object.entries(symbolMap)) {
      if (lower.includes(key)) return symbol;
    }
    return currencyName || 'د.إ';
  };

  const currencySymbol = getCurrencySymbol(formData.CurrencyName);

  // Initialize view mode data or reset for create mode
  useEffect(() => {
    // Only run when modal opens or modalAction changes
    const modalJustOpened = open && !prevOpenRef.current;
    const modalActionChanged = modalAction !== prevModalActionRef.current;

    prevOpenRef.current = open;
    prevModalActionRef.current = modalAction;

    if (!open) {
      initializedRef.current = false;
      return;
    }

    // Only initialize once when modal opens or action changes
    if (!initializedRef.current || modalJustOpened || modalActionChanged) {
      initializedRef.current = true;

      const initializeData = async () => {
      if ((modalAction === 'view' || modalAction === 'edit') && selectedSaleReturn && saleReturnDetails) {
        // Format bill number with prefix and fin year
        const billPrefix = selectedSaleReturn.BillPrefix || '';
        const billNumber = selectedSaleReturn.BillNumber || '';
        const finYear = selectedSaleReturn.FinYear || '';
        const fullBillNo = billPrefix && finYear 
          ? `${billPrefix}/${billNumber}/${finYear}` 
          : billNumber;

        // Resolve customer name - try multiple fields, fallback to customers list
        let customerName = selectedSaleReturn.CustomerName || selectedSaleReturn.MasterName || selectedSaleReturn.Name || '';
        if (!customerName && selectedSaleReturn.MasterId) {
          const cust = customers.find(c => (c.MasterId || c.Id) === parseInt(selectedSaleReturn.MasterId));
          customerName = cust ? cust.Name : '';
        }

        setFormData({
          billNo: fullBillNo,
          billPrefix: billPrefix,
          BillNumber: billNumber,
          FinYear: finYear,
          billDate: selectedSaleReturn.BillDate?.split('T')[0] || '',
          SaleId: selectedSaleReturn.SaleId || '',
          SaleNo: selectedSaleReturn.SaleNo || '',
          customerId: selectedSaleReturn.MasterId || '',
          customer: customerName,
          vatNo: selectedSaleReturn.VATNumber || '',
          invType: selectedSaleReturn.InvoiceTypeName || '',
          vatType: selectedSaleReturn.VATCalculationType === 2 ? 'Including Tax' : 'Excluding Tax',
          SalesManId: selectedSaleReturn.SalesManId || '',
          CurrencyTypeId: selectedSaleReturn.CurrencyTypeId || null,
          CurrencyName: selectedSaleReturn.CurrencyName || ''
        });

        const loadedItems = saleReturnDetails.map(d => ({
          id: d.Id || 0,
          itemId: d.ItemId,
          item: d.ItemName || '',
          hsnCode: d.HSNCode || '',
          vatPercent: d.VATRate || d.GSTRate || 0,
          SaledQty: d.SaleQuantity || d.SoldQuantity || 0,
          alreadyReturnedQty: d.ReturnedQuantity || 0,
          availableQty: (d.SaleQuantity || d.SoldQuantity || 0) - (d.ReturnedQuantity || 0),
          rcvdQty: d.Quantity || 0,
          rate: d.Rate || 0,
          amount: d.Amount || 0,
          discPercent: d.DiscountPercentage || 0,
          discAmt: d.DiscountAmount || 0,
          taxable: d.TaxableAmount || 0,
          vatAmount: d.VATAmount || 0,
          igst: d.IGSTAmount || 0,
          total: d.TotalAmount || 0
        }));

        // If SaleId exists, fetch sale details to get Sold Qty and calculate Returned/Available
        const saleId = selectedSaleReturn.SaleId;
        if (saleId && token) {
          try {
            const saleResponse = await fetchData('SaleApi', `/${saleId}`, token);
            const saleData = saleResponse.response_data || saleResponse;
            const saleDetails = saleData.SaleDetails || [];

            // Fetch existing returns for this sale to get returned quantities
            const returnsResponse = await fetchData('SaleReturnApi', `/ActiveList/${companyId}/${branchId}/2020-01-01/${new Date().toISOString().split('T')[0]}`, token);
            const allReturns = returnsResponse.response_data || returnsResponse || [];
            const filteredReturns = Array.isArray(allReturns)
              ? allReturns.filter(ret => ret.SaleId === parseInt(saleId) && ret.Id !== selectedSaleReturn.Id)
              : [];

            // Get returned quantities from other returns (excluding current one)
            const returnedQuantities = {};
            await Promise.all(filteredReturns.map(async (ret) => {
              try {
                const detailResponse = await fetchData('SaleReturnApi', `/${ret.Id}`, token);
                const detailData = detailResponse.response_data || detailResponse;
                const retDetails = detailData.SaleReturnDetails || [];
                retDetails.forEach(detail => {
                  const itemId = detail.ItemId;
                  returnedQuantities[itemId] = (returnedQuantities[itemId] || 0) + (parseFloat(detail.Quantity) || 0);
                });
              } catch (e) { /* ignore */ }
            }));

            // Update loaded items with sale quantities
            const updatedItems = loadedItems.map(item => {
              const saleDetail = saleDetails.find(sd => sd.ItemId === item.itemId);
              const soldQty = saleDetail ? (saleDetail.Quantity || saleDetail.ReceivedQuantity || 0) : 0;
              const returnedQty = returnedQuantities[item.itemId] || 0;
              const availableQty = Math.max(0, soldQty - returnedQty - (parseFloat(item.rcvdQty) || 0));

              return {
                ...item,
                SaledQty: soldQty,
                alreadyReturnedQty: returnedQty,
                availableQty: availableQty
              };
            });

            setItems(updatedItems);
          } catch (error) {
            console.error('Error fetching sale details for view:', error);
            setItems(loadedItems);
          }
        } else {
          setItems(loadedItems);
        }

        const loadedCharges = {
          freight: selectedSaleReturn.FreightAmount || 0,
          packing: selectedSaleReturn.PackingAmount || 0,
          tcsAmount: selectedSaleReturn.TCSAmount || 0,
          cashAmount: selectedSaleReturn.CashAmount || 0,
          upiAmount: selectedSaleReturn.UPIAmount || 0,
          cardAmount: selectedSaleReturn.CardAmount || 0,
          bankId: selectedSaleReturn.BankId || '',
          remarks: selectedSaleReturn.Remarks || ''
        };

        setAdditionalCharges(loadedCharges);

        // Calculate summary from loaded items and charges
        calculateSummary(loadedItems, loadedCharges);
      } else if (modalAction === 'create') {
        // Reset form when opening in create mode
        setFormData({
          billNo: '',
          billDate: new Date().toISOString().split('T')[0],
          SaleId: '',
          SaleNo: '',
          SaleBillPrefix: '',
          SaleBillNumber: '',
          SaleFinYear: '',
          customerId: '',
          customer: '',
          vatNo: '',
          invType: '',
          vatType: 'Excluding Tax',
          SalesManId: ''
        });
        setItems([]);
        setAdditionalCharges({
          freight: 0,
          packing: 0,
          tcsAmount: 0,
          cashAmount: 0,
          upiAmount: 0,
          cardAmount: 0,
          bankId: '',
          remarks: ''
        });
        setSummary({
          subtotal: 0,
          discount: 0,
          taxable: 0,
          vatamount: 0,
          igst: 0,
          freightVAT: 0,
          packingVAT: 0,
          effectiveVATRate: 5,
          tcs: 0,
          roundOff: 0,
          total: 0,
          paid: 0,
          balance: 0
        });
        setValidationError('');
      }
      };
      initializeData();
    }
  }, [modalAction, selectedSaleReturn, saleReturnDetails, open]);

  // Fetch items list
  useEffect(() => {
    if (modalAction === 'view') return; // Skip fetching in view mode

    const fetchItems = async () => {
      if (!companyId || !token || !open) return;

      try {
        const response = await fetchData('ItemApi', `/${companyId}`, token);
        const itemsData = response.response_data || response || [];
        console.log('Items fetched:', itemsData);
        setItemsList(Array.isArray(itemsData) ? itemsData : []);
      } catch (error) {
        console.error('Error fetching items:', error);
        setItemsList([]);
      }
    };

    fetchItems();
  }, [companyId, token, open]);

  // Fetch Staff/Salesman list
  useEffect(() => {
    const fetchStaff = async () => {
      if (!companyId || !token || !open) return;

      try {
        const response = await fetchData('StaffApi', `/StaffList/${companyId}`, token);
        const staffData = response.response_data || response || [];
        setStaffList(Array.isArray(staffData) ? staffData : []);
      } catch (error) {
        console.error('Error fetching staff:', error);
        setStaffList([]);
      }
    };

    fetchStaff();
  }, [companyId, token, open]);

  // Fetch Sales list based on selected customer
  useEffect(() => {
    if (modalAction === 'view') return; // Skip fetching in view mode

    const fetchSales = async () => {
      // Only fetch if customer is selected
      if (!companyId || !branchId || !token || !open || !formData.customerId) {
        setSales([]);
        return;
      }

      try {
        // Fetch sales for the specific customer
        const response = await fetchData('SaleApi', `/Sales/${companyId}/${branchId}/${formData.customerId}/Sales`, token);
        const SalesData = response.response_data || response || [];
        console.log('Sales fetched for customer:', formData.customerId, SalesData);
        let filteredSales = Array.isArray(SalesData) ? SalesData : [];

        // Filter by SalesManId if selected
        if (formData.SalesManId) {
          filteredSales = filteredSales.filter(sale =>
            sale.SalesManId === parseInt(formData.SalesManId) ||
            sale.CreatedBy === parseInt(formData.SalesManId)
          );
          console.log('Sales filtered by SalesManId:', formData.SalesManId, filteredSales);
        }

        setSales(filteredSales);
      } catch (error) {
        console.error('Error fetching Sales:', error);
        setSales([]);
      }
    };

    fetchSales();
  }, [companyId, branchId, token, open, formData.customerId, formData.SalesManId]);

  // Fetch MaxBillNo
  useEffect(() => {
    if (modalAction === 'view') return; // Skip fetching in view mode

    const fetchMaxBillNo = async () => {
      if (!companyId || !branchId || !token || !open) return;

      try {
        const response = await fetchData('SaleReturnApi', `/MaxBillno/${companyId}/${branchId}`, token);
        const data = response.response_data || response || {};
        const billNumber = data.BillNumber || data.billNumber || 1;
        const billPrefix = data.BillPrefix || data.billPrefix || '';
        const finYear = data.FinYear || data.finYear || '';

        // Format: prefix/billno/finyear
        const fullBillNo = `${billPrefix}/${billNumber}/${finYear}`;
        setFormData(prev => ({ ...prev, billNo: fullBillNo, billPrefix: billPrefix, BillNumber: billNumber, FinYear: finYear }));
      } catch (error) {
        console.error('Error fetching MaxBillNo:', error);
        setFormData(prev => ({ ...prev, billNo: '1' }));
      }
    };

    fetchMaxBillNo();
  }, [companyId, branchId, token, open]);

  // Fetch Banks list
  useEffect(() => {
    if (modalAction === 'view') return; // Skip fetching in view mode

    const fetchBanks = async () => {
      if (!companyId || !token || !open) return;

      try {
        const response = await fetchData('BankApi', `/${companyId}`, token);
        const banksData = response.response_data || response || [];
        console.log('Banks fetched:', banksData);
        setBanks(Array.isArray(banksData) ? banksData : []);
      } catch (error) {
        console.error('Error fetching banks:', error);
        setBanks([]);
      }
    };

    fetchBanks();
  }, [companyId, token, open, modalAction]);

  // Handle customer selection - auto-fill InvoiceTypeName and GSTNumber
  const handlecustomerChange = (customerId) => {
    console.log('customer selected:', customerId);

    if (!customerId) {
      setFormData(prev => ({
        ...prev,
        customerId: '',
        customer: '',
        vatNo: '',
        invType: '',
        SaleId: '',
        SaleNo: '',
        SaleBillPrefix: '',
        SaleBillNumber: '',
        SaleFinYear: '',
        SalesManId: ''
      }));
      setItems([]);
      setAdditionalCharges(prev => ({
        ...prev,
        freight: 0,
        packing: 0,
        cashAmount: 0,
        upiAmount: 0,
        cardAmount: 0,
        bankId: ''
      }));
      setSummary({
        subtotal: 0, discount: 0, taxable: 0, vatamount: 0, igst: 0,
        freightVAT: 0, packingVAT: 0, effectiveVATRate: 5, tcs: 0,
        roundOff: 0, total: 0, paid: 0, balance: 0
      });
      return;
    }

    const selectedcustomer = customers.find(s => (s.MasterId || s.Id) === parseInt(customerId));

    if (selectedcustomer) {
      setFormData(prev => ({
        ...prev,
        customerId: customerId,
        customer: selectedcustomer.Name || '',
        vatNo: selectedcustomer.VATNumber || selectedcustomer.VATNo || '',
        invType: selectedcustomer.InvoiceTypeName || selectedcustomer.InvoiceType || '',
        SaleId: '',
        SaleNo: '',
        SaleBillPrefix: '',
        SaleBillNumber: '',
        SaleFinYear: '',
        SalesManId: ''
      }));
      setItems([]);
      setAdditionalCharges(prev => ({
        ...prev,
        freight: 0,
        packing: 0,
        cashAmount: 0,
        upiAmount: 0,
        cardAmount: 0,
        bankId: ''
      }));
      setSummary({
        subtotal: 0, discount: 0, taxable: 0, vatamount: 0, igst: 0,
        freightVAT: 0, packingVAT: 0, effectiveVATRate: 5, tcs: 0,
        roundOff: 0, total: 0, paid: 0, balance: 0
      });
    }
  };

  // Recalculate when InvoiceType or GSTType changes
  useEffect(() => {
    if (items.length > 0 && (formData.invType || formData.gstType)) {
      const useIGST = isIGSTInvoice();

      const updatedItems = items.map(item => {
        const qty = Math.max(0, parseFloat(item.rcvdQty) || 0);
        const rate = Math.max(0, parseFloat(item.rate) || 0);
        const discPercent = Math.max(0, parseFloat(item.discPercent) || 0);
        const vatRate = Math.max(0, parseFloat(item.vatPercent) || 0);
        const isIncludingTax = formData.Type === 'Including Tax';

        let taxableAmount, vatAmount, discountAmount;

        if (isIncludingTax) {
          const totalWithVat = qty * rate;
          const baseAmount = totalWithVat / (1 + vatRate / 100);
          discountAmount = (baseAmount * discPercent) / 100;
          taxableAmount = baseAmount - discountAmount;
          vatAmount = (taxableAmount * vatRate) / 100;
        } else {
          const baseAmount = qty * rate;
          discountAmount = (baseAmount * discPercent) / 100;
          taxableAmount = baseAmount - discountAmount;
          vatAmount = (taxableAmount * vatRate) / 100;
        }

        return {
          ...item,
          discAmt: Math.round(discountAmount * 100) / 100,
          taxable: Math.round(taxableAmount * 100) / 100,
          //cgst: useIGST ? 0 : Math.round((gstAmount / 2) * 100) / 100,
          vatAmount: useIGST ? 0 : Math.round((vatAmount)),
          igst: useIGST ? Math.round(vatAmount * 100) / 100 : 0,
          total: Math.round((taxableAmount + vatAmount) * 100) / 100
        };
      });

      setItems(updatedItems);
      calculateSummary(updatedItems, additionalCharges);
    }
  }, [formData.invType, formData.gstType]);

  const handleChange = (field, value) => {
    console.log('handleChange called:', field, value);
    if (field === 'customerId') {
      handlecustomerChange(value);
    } else if (field === 'SaleId') {
      handleSaleChange(value);
    } else if (field === 'SalesManId') {
      // When salesman changes, clear Sale selection
      setFormData(prev => ({ ...prev, SalesManId: value, SaleId: '', SaleNo: '' }));
      setItems([]);
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  // Fetch single Sale details
  const fetchSingleSale = async (SaleId) => {
    try {
      const response = await fetchData('SaleApi', `/${SaleId}`, token);
      return response.response_data || response;
    } catch (error) {
      console.error('Error fetching single Sale:', error);
      return null;
    }
  };

  // Fetch sale returns for a specific sale to calculate already returned quantities
  const fetchSaleReturnsForSale = async (SaleId) => {
    try {
      // Fetch all active sale returns for the branch
      // We'll filter by SaleId on the client side since there's no specific endpoint
      const response = await fetchData('SaleReturnApi', `/ActiveList/${companyId}/${branchId}/2020-01-01/${new Date().toISOString().split('T')[0]}`, token);
      const allReturns = response.response_data || response || [];

      // Filter returns that match the SaleId
      const filteredReturns = Array.isArray(allReturns)
        ? allReturns.filter(ret => ret.SaleId === parseInt(SaleId))
        : [];

      console.log('All sale returns:', allReturns.length);
      console.log('Filtered sale returns for SaleId', SaleId, ':', filteredReturns.length);

      // For each return, fetch its details
      const returnsWithDetails = await Promise.all(
        filteredReturns.map(async (ret) => {
          try {
            const detailResponse = await fetchData('SaleReturnApi', `/${ret.Id}`, token);
            const detailData = detailResponse.response_data || detailResponse;
            return {
              ...ret,
              SaleReturnDetails: detailData.SaleReturnDetails || []
            };
          } catch (error) {
            console.error('Error fetching return details:', error);
            return { ...ret, SaleReturnDetails: [] };
          }
        })
      );

      return returnsWithDetails;
    } catch (error) {
      console.error('Error fetching sale returns for sale:', error);
      return [];
    }
  };

  // Handle Sale selection - auto-fill customer and other details
  const handleSaleChange = async (SaleId) => {
    console.log('Sale selected:', SaleId);

    if (!SaleId) {
      // Reset Sale-related fields
      setFormData(prev => ({
        ...prev,
        SaleId: '',
        SaleNo: ''
      }));
      setItems([]); // Clear items
      // Clear payment fields
      setAdditionalCharges(prev => ({
        ...prev,
        cashAmount: 0,
        upiAmount: 0,
        cardAmount: 0,
        bankId: ''
      }));
      return;
    }

    try {
      // Fetch full Sale details using the API
      const SaleDetails = await fetchSingleSale(SaleId);

      if (SaleDetails) {
        const SaleData = SaleDetails.Sale || SaleDetails;
        const details = SaleDetails.SaleDetails || [];

        console.log('Fetched Sale data:', SaleData);
        console.log('Fetched Sale details:', details);

        // Fetch existing sale returns for this sale to calculate already returned quantities
        const existingReturns = await fetchSaleReturnsForSale(SaleId);
        console.log('Existing sale returns:', existingReturns);

        // Calculate total returned quantity per item
        const returnedQuantities = {};
        if (Array.isArray(existingReturns)) {
          existingReturns.forEach(returnDoc => {
            const returnDetails = returnDoc.SaleReturnDetails || [];
            returnDetails.forEach(detail => {
              const itemId = detail.ItemId;
              const returnedQty = parseFloat(detail.Quantity) || 0;
              returnedQuantities[itemId] = (returnedQuantities[itemId] || 0) + returnedQty;
            });
          });
        }

        console.log('Calculated returned quantities:', returnedQuantities);

        // Log first detail to see available fields
        if (details.length > 0) {
          console.log('First detail item fields:', details[0]);
        }

        // Format SaleNo as BillPrefix/BillNumber/FinYear
        const billPrefix = SaleData.BillPrefix || '';
        const billNumber = SaleData.BillNumber || '';
        const finYear = SaleData.FinYear || '';
        const formattedSaleNo = `${billPrefix}/${billNumber}/${finYear}`;

        // Auto-fill customer and other details from Sale
        setFormData(prev => ({
          ...prev,
          SaleId: SaleId,
          SaleNo: formattedSaleNo,
          SaleBillPrefix: billPrefix,
          SaleBillNumber: billNumber,
          SaleFinYear: finYear,
          customerId: SaleData.MasterId || '',
          customer: SaleData.CustomerName || '',
          vatNo: SaleData.VATNumber || '',
          invType: SaleData.InvoiceTypeName || '',
          vatType: SaleData.VATCalculationType === 2 ? 'Including Tax' : 'Excluding Tax',
          SalesManId: SaleData.SalesManId || ''
        }));

        // Clear payment fields when Sale No changes
        setAdditionalCharges(prev => ({
          ...prev,
          cashAmount: 0,
          upiAmount: 0,
          cardAmount: 0,
          bankId: ''
        }));

        // Populate items from Sale details
        const SaleItems = details.map(detail => {
          const itemId = detail.ItemId || '';
          const saledQty = detail.Quantity || detail.ReceivedQuantity || detail.SaleQuantity || 0;
          const alreadyReturnedQty = returnedQuantities[itemId] || 0;
          const availableQty = Math.max(0, saledQty - alreadyReturnedQty);

          return {
            id: Date.now() + Math.random(), // Unique ID
            itemId: itemId,
            item: detail.ItemName || '',
            hsnCode: detail.HSNCode || '',
            SaledQty: saledQty, // Original sold quantity
            alreadyReturnedQty: alreadyReturnedQty, // Already returned quantity
            availableQty: availableQty, // Available for return
            rcvdQty: 0, // Return quantity starts at 0
            rate: detail.Rate || 0,
            amount: 0,
            discPercent: detail.DiscountPercentage || 0,
            discAmt: 0,
            vatPercent: detail.VATRate || 0,
            taxable: 0,
            // cgst: 0,
            vatAmount: 0,
            igst: 0,
            total: 0
          };
        });

        setItems(SaleItems);
        calculateSummary(SaleItems, { ...additionalCharges, cashAmount: 0, upiAmount: 0, cardAmount: 0, bankId: '' });
      }
    } catch (error) {
      console.error('Error handling Sale selection:', error);
    }
  };

  const handleAdditionalChargeChange = (field, value) => {
    // For remarks field, keep as string; for others, convert to number
    const processedValue = field === 'remarks' || field === 'bankId' ? value : (parseFloat(value) || 0);

    setAdditionalCharges(prev => {
      const updated = { ...prev, [field]: processedValue };

      // Validate payment amounts don't exceed total
      if (['cashAmount', 'upiAmount', 'cardAmount'].includes(field)) {
        const totalAmount = parseFloat(summary.total) || 0;
        const cashAmount = field === 'cashAmount' ? processedValue : (parseFloat(prev.cashAmount) || 0);
        const upiAmount = field === 'upiAmount' ? processedValue : (parseFloat(prev.upiAmount) || 0);
        const cardAmount = field === 'cardAmount' ? processedValue : (parseFloat(prev.cardAmount) || 0);
        const totalPayments = cashAmount + upiAmount + cardAmount;

        // If total payments exceed total amount, cap the current field
        if (totalPayments > totalAmount) {
          const otherPayments = totalPayments - processedValue;
          const maxAllowed = Math.max(0, totalAmount - otherPayments);
          updated[field] = Math.round(maxAllowed * 100) / 100;
        }
      }

      calculateSummary(items, updated);
      return updated;
    });
  };

  const handleAddItem = () => {
    // Validation: Check if the last row has data before adding a new row
    if (items.length > 0) {
      const lastItem = items[items.length - 1];

      // Check if the last item has an item selected and return quantity > 0
      if (!lastItem.itemId || lastItem.itemId === '') {
        setValidationError('Please select an item in the current row before adding a new one.');
        return;
      }

      if (!lastItem.rcvdQty || parseFloat(lastItem.rcvdQty) <= 0) {
        setValidationError('Please enter Return Qty in the current row before adding a new one.');
        return;
      }
    }

    // Clear validation error if validation passes
    setValidationError('');

    setItems(prev => [...prev, {
      id: Date.now(),
      itemId: '',
      item: '',
      hsnCode: '',
      SaledQty: 0,
      rcvdQty: 0,
      rate: 0,
      amount: 0,
      discPercent: 0,
      discAmt: 0,
      vatPercent: 0,
      taxable: 0,
      //cgst: 0,
      vatAmount: 0,
      igst: 0,
      total: 0
    }]);
  };

  const handleDeleteItem = (id) => {
    const updatedItems = items.filter(item => item.id !== id);
    setItems(updatedItems);
    calculateSummary(updatedItems, additionalCharges);
  };

  const handleItemChange = (id, field, value) => {
    const updatedItems = items.map(item => {
      if (item.id !== id) return item;

      const updated = { ...item, [field]: value };

      // If item is selected from dropdown, populate item details
      if (field === 'itemId' && value) {
        const selectedItem = itemsList.find(i => i.Id === parseInt(value));
        if (selectedItem) {
          console.log('Selected item data:', selectedItem);
          updated.item = selectedItem.Name || '';
          updated.hsnCode = selectedItem.HSNCode || selectedItem.HSN || '';
          updated.vatPercent = selectedItem.VATRate || selectedItem.GST || 0;
          // Try multiple rate fields
          updated.rate = selectedItem.SaleRate || selectedItem.Rate || selectedItem.SaleRate || selectedItem.MRP || 0;
          console.log('Updated rate:', updated.rate);
        }
      }

      // Validate return quantity doesn't exceed available quantity (sold - already returned)
      if (field === 'rcvdQty') {
        const returnQty = parseFloat(value) || 0;
        const availableQty = parseFloat(item.availableQty) || parseFloat(item.SaledQty) || 0;

        // If available quantity exists and return quantity exceeds it, cap it
        if (availableQty > 0 && returnQty > availableQty) {
          updated.rcvdQty = availableQty;
        }
      }

      // Recalculate when quantity, rate, discount, or GST changes
      if (['rcvdQty', 'rate', 'discPercent', 'discAmt', 'vatPercent', 'itemId'].includes(field)) {
        const qty = Math.max(0, parseFloat(updated.rcvdQty) || 0);
        const rate = Math.max(0, parseFloat(updated.rate) || 0);
        const vatRate = Math.max(0, parseFloat(updated.vatPercent) || 0);
        const isIncludingTax = formData.vatType === 'Including Tax';
        const useIGST = isIGSTInvoice();

        let taxableAmount, vatAmount, amount, discountAmount, discountPercent;
        let baseAmount;

        if (isIncludingTax) {
          // Rate includes VAT - reverse calculate
          const totalWithvat = qty * rate;
          baseAmount = totalWithvat / (1 + vatRate / 100);
        } else {
          // Rate excludes GST - forward calculate
          baseAmount = qty * rate;
        }

        // If discAmt is being changed directly, calculate discPercent from it
        if (field === 'discAmt') {
          discountAmount = Math.max(0, parseFloat(updated.discAmt) || 0);
          // Calculate discount percentage from amount
          discountPercent = baseAmount > 0 ? (discountAmount / baseAmount) * 100 : 0;
          updated.discPercent = Math.round(discountPercent * 100) / 100;
        } else {
          // Calculate discAmt from discPercent
          discountPercent = Math.max(0, parseFloat(updated.discPercent) || 0);
          discountAmount = (baseAmount * discountPercent) / 100;
          updated.discAmt = Math.round(discountAmount * 100) / 100;
        }

        taxableAmount = baseAmount - discountAmount;
        vatAmount = (taxableAmount * vatRate) / 100;
        amount = taxableAmount + vatAmount;

        // Round values
        updated.amount = Math.round((qty * rate) * 100) / 100; // Add rounded amount field
        updated.taxable = Math.round(taxableAmount * 100) / 100;
        updated.vatAmount = useIGST ? 0 : Math.round(vatAmount * 100) / 100;
        // updated.sgst = useIGST ? 0 : Math.round((vatAmount / 2) * 100) / 100;
        updated.igst = useIGST ? Math.round(vatAmount * 100) / 100 : 0;
        updated.total = Math.round(amount * 100) / 100;
      }

      return updated;
    });

    setItems(updatedItems);
    calculateSummary(updatedItems, additionalCharges);
  };

  const calculateSummary = (itemsList, charges) => {
    const useIGST = isIGSTInvoice();

    // Find the maximum GST rate from items
    const maxItemVATRate = itemsList.reduce((max, item) => {
      const vatRate = Math.max(0, parseFloat(item.vatPercent) || 0);
      return Math.max(max, vatRate);
    }, 0);

    // Default freight and packing VAT rate is 18%, but use max item VAT if it's higher
    const defaultFreightPackingVATRate = 5;
    const effectiveVATRate = Math.max(maxItemVATRate, defaultFreightPackingVATRate);

    const itemTotals = itemsList.reduce((acc, item) => {
      const qty = Math.max(0, parseFloat(item.rcvdQty) || 0);
      const rate = Math.max(0, parseFloat(item.rate) || 0);
      const discPercent = Math.max(0, parseFloat(item.discPercent) || 0);
      const vatRate = Math.max(0, parseFloat(item.vatPercent) || 0);
      const isIncludingTax = formData.vatType === 'Including Tax';

      let taxableAmount, vatAmount, discountAmount;

      if (isIncludingTax) {
        const totalWithGst = qty * rate;
        const baseAmount = totalWithGst / (1 + vatRate / 100);
        discountAmount = (baseAmount * discPercent) / 100;
        taxableAmount = baseAmount - discountAmount;
        vatAmount = (taxableAmount * vatRate) / 100;
      } else {
        const baseAmount = qty * rate;
        discountAmount = (baseAmount * discPercent) / 100;
        taxableAmount = baseAmount - discountAmount;
        vatAmount = (taxableAmount * vatRate) / 100;
      }

      return {
        subtotal: acc.subtotal + (qty * rate),
        discount: acc.discount + discountAmount,
        taxable: acc.taxable + taxableAmount,
        // cgst: acc.cgst + (useIGST ? 0 : gstAmount / 2),
        vatAmount: acc.vatAmount + (useIGST ? 0 : vatAmount),
        igst: acc.igst + (useIGST ? vatAmount : 0)
      };
    }, { subtotal: 0, discount: 0, taxable: 0, vatAmount: 0, igst: 0 });

    const freight = parseFloat(charges.freight) || 0;
    const packing = parseFloat(charges.packing) || 0;
    const tcs = parseFloat(charges.tcsAmount) || 0;
    const cashAmount = parseFloat(charges.cashAmount) || 0;
    const upiAmount = parseFloat(charges.upiAmount) || 0;
    const cardAmount = parseFloat(charges.cardAmount) || 0;
    const paid = cashAmount + upiAmount + cardAmount;

    // Calculate freight and packing VAT
    const freightVAT = (freight * effectiveVATRate) / 100;
    const packingVAT = (packing * effectiveVATRate) / 100;
    const totalFreightPackingVAT = freightVAT + packingVAT;

    const grandTotal = itemTotals.taxable + itemTotals.vatAmount + itemTotals.igst + freight + packing + totalFreightPackingVAT + tcs;
    const roundOff = Math.round(grandTotal) - grandTotal;
    const total = Math.round(grandTotal);

    setSummary({
      subtotal: Math.round(itemTotals.subtotal * 100) / 100,
      discount: Math.round(itemTotals.discount * 100) / 100,
      taxable: Math.round(itemTotals.taxable * 100) / 100,
      vatamount: Math.round((itemTotals.vatAmount + (useIGST ? 0 : totalFreightPackingVAT)) * 100) / 100,
      //sgst: Math.round((itemTotals.sgst + (useIGST ? 0 : totalFreightPackingGST / 2)) * 100) / 100,
      igst: Math.round((itemTotals.igst + (useIGST ? totalFreightPackingVAT : 0)) * 100) / 100,
      freightVAT: Math.round(freightVAT * 100) / 100,
      packingVAT: Math.round(packingVAT * 100) / 100,
      effectiveVATRate: effectiveVATRate,
      tcs: tcs,
      roundOff: Math.round(roundOff * 100) / 100,
      total: total,
      paid: paid,
      balance: Math.round((total - paid) * 100) / 100
    });
  };

  const handleSubmit = async () => {
    // Clear previous validation errors
    setValidationError('');

    // Validation: Check if there are any items
    if (items.length === 0) {
      setValidationError('Please add at least one item to the return.');
      return;
    }

    // Validation: Check if at least one item has Return Qty > 0
    const itemsWithReturnQty = items.filter(item => (parseFloat(item.rcvdQty) || 0) > 0);

    if (itemsWithReturnQty.length === 0) {
      setValidationError('Please enter Return Qty for at least one item.');
      return;
    }

    // Validation: Check if items with Return Qty have Item selected
    const invalidItems = [];
    itemsWithReturnQty.forEach((item, index) => {
      if (!item.itemId || item.itemId === '') {
        invalidItems.push(index + 1);
      }
    });

    if (invalidItems.length > 0) {
      setValidationError(`Please select an item for row(s): ${invalidItems.join(', ')}`);
      return;
    }

    // Additional validation
    const validationErrors = [];

    if (!formData.billNo?.trim()) {
      validationErrors.push('Return Number is required');
    }

    if (!formData.billDate) {
      validationErrors.push('Return Date is required');
    }

    // Payment validation
    const totalAmount = parseFloat(summary.total) || 0;
    const cashAmount = parseFloat(additionalCharges.cashAmount) || 0;
    const upiAmount = parseFloat(additionalCharges.upiAmount) || 0;
    const cardAmount = parseFloat(additionalCharges.cardAmount) || 0;
    const totalPayments = cashAmount + upiAmount + cardAmount;

    if (totalPayments > totalAmount) {
      validationErrors.push(`Total payment amount (${currencySymbol} ${totalPayments.toFixed(2)}) cannot exceed total amount (${currencySymbol} ${totalAmount.toFixed(2)})`);
    }

    // Validate bank selection if UPI or Card amount is entered
    if ((upiAmount > 0 || cardAmount > 0) && !additionalCharges.bankId) {
      validationErrors.push('Bank selection is required when UPI or Card payment is made');
    }

    if (validationErrors.length > 0) {
      setValidationError(validationErrors.join(', '));
      return;
    }

    setLoading(true);
    setLoadingMessage(modalAction === 'edit' ? 'Updating Sale return...' : 'Creating Sale return...');

    try {
      const vatCalculationType = formData.vatType === 'Including Tax' ? 2 : 1;

      // Determine SaleNo based on whether SaleId is selected
      const saleId = parseInt(formData.SaleId) || 0;
      const saleNo = saleId > 0 ? `${formData.SaleBillPrefix}/${formData.SaleBillNumber}/${formData.SaleFinYear}` : '';

      console.log('SaleNo being sent:', saleNo);
      console.log('SaleId being sent:', saleId);

      const dataToSend = {
        SaleReturn: {
          Id: modalAction === 'edit' ? selectedSaleReturn.Id : 0,
          CompanyId: companyId,
          BranchId: branchId,
          SaleId: saleId,
          SaleNo: saleNo,
          SaleDate: formData.SaleDate || null,
          SalesManId: formData.SalesManId ? parseInt(formData.SalesManId) : null,
          BillNumber: formData.BillNumber || formData.billNo,
          BillPrefix: formData.billPrefix || 'SR',
          FinYear: formData.FinYear || '',
          BillDate: formData.billDate,
          MasterId: parseInt(formData.customerId),
          MasterName: formData.customer || '',
          CustomerName: formData.customer || '',
          InvoiceTypeId: 0,
          VATNumber: formData.vatNo || '',
          VATCalculationType: vatCalculationType,
          VATCalculationTypeName: formData.vatType || 'Excluding Tax',
          Amount: parseFloat(summary.subtotal) || 0,
          DiscountAmount: parseFloat(summary.discount) || 0,
          TaxableAmount: parseFloat(summary.taxable) || 0,
          VATAmount: parseFloat(summary.vatamount) || 0,
          IGSTAmount: parseFloat(summary.igst) || 0,
          FreightAmount: parseFloat(additionalCharges.freight) || 0,
          FreightVATRate: parseFloat(summary.effectiveVATRate) || 0,
          FreightVATAmount: parseFloat(summary.freightVAT) || 0,
          PackingAmount: parseFloat(additionalCharges.packing) || 0,
          PackingVATRate: parseFloat(summary.effectiveVATRate) || 0,
          PackingVATAmount: parseFloat(summary.packingVAT) || 0,
          TCSAmount: parseFloat(additionalCharges.tcsAmount) || 0,
          RoundOff: parseFloat(summary.roundOff) || 0,
          TotalAmount: parseFloat(summary.total) || 0,
          CashAmount: parseFloat(additionalCharges.cashAmount) || 0,
          UPIAmount: parseFloat(additionalCharges.upiAmount) || 0,
          CardAmount: parseFloat(additionalCharges.cardAmount) || 0,
          BankId: parseInt(additionalCharges.bankId) || 0,
          PaidAmount: parseFloat(summary.paid) || 0,
          BalanceAmount: parseFloat(summary.balance) || 0,
          Remarks: additionalCharges.remarks || '',
          CurrencyTypeId: formData.CurrencyTypeId ? parseInt(formData.CurrencyTypeId) : null,
          CurrencyName: formData.CurrencyName || '',
          IsActive: true,
          CreatedBy: userId
        },
        SaleReturnDetails: itemsWithReturnQty.map(item => ({
          Id: modalAction === 'edit' ? item.id : 0,
          CompanyId: companyId,
          BranchId: branchId,
          SaleReturnId: modalAction === 'edit' ? selectedSaleReturn.Id : 0,
          ItemId: parseInt(item.itemId),
          ItemName: item.item || '',
          BatchNumber: item.batchNumber || '',
          SaleId: parseInt(formData.SaleId) || 0,
          SaleQuantity: parseFloat(item.SaledQty) || 0,
          Quantity: parseFloat(item.rcvdQty) || 0,
          ReturnStockQuantity: parseFloat(item.rcvdQty) || 0,
          Rate: parseFloat(item.rate) || 0,
          Amount: parseFloat((item.rcvdQty || 0) * (item.rate || 0)) || 0,
          DiscountPercentage: parseFloat(item.discPercent) || 0,
          DiscountAmount: parseFloat(item.discAmt) || 0,
          HSNCode: item.hsnCode || '',
          VATRate: parseFloat(item.vatPercent) || 0,
          TaxableAmount: parseFloat(item.taxable) || 0,
          VATAmount: parseFloat(item.vatAmount) || 0,
          IGSTAmount: parseFloat(item.igst) || 0,
          TotalAmount: parseFloat(item.total) || 0,
          IsActive: true,
          CreatedBy: userId
        }))
      };

      //console.log('Data to send:', dataToSend);

      if (modalAction === 'edit') {
        // Update existing sale return
        await updateData('SaleReturnApi', `${selectedSaleReturn.Id}`, dataToSend, token);
        setSuccess('Sale return updated successfully!');
      } else {
        // Create new sale return
        await postData('SaleReturnApi', '', dataToSend, token);
        setSuccess('Sale return created successfully!');
      }

      setCreateModalOpen(false);
      fetchSaleReturns();
      onClose();
    } catch (error) {
      console.error(`Error ${modalAction === 'edit' ? 'updating' : 'creating'} Sale return:`, error);
      setError(`Failed to ${modalAction === 'edit' ? 'update' : 'create'} Sale return.`);
    }

    setLoading(false);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '95vh',
          height: '95vh'
        }
      }}
    >
      <DialogTitle sx={{
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
        borderBottom: '1px solid #e0e0e0',
        p: 0.75,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <Typography variant="h6" sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#f5f5f5' }}>
          {modalAction === 'view' ? '👁️ View Sale Return' : modalAction === 'edit' ? '✏️ Edit Sale Return' : '⭐ Create Sale Return'}
        </Typography>
        <IconButton onClick={onClose} size="small" sx={{ p: 0.2, color: '#fff' }}>
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{
        p: { xs: 1, md: 0.5 },
        bgcolor: '#fafafa',
        overflow: 'auto'
      }}>
        {/* Validation Error Message */}
        {validationError && (
          <Box sx={{ mb: 2 }}>
            <Alert severity="error" onClose={() => setValidationError('')}>
              {validationError}
            </Alert>
          </Box>
        )}

        {/* Sale Information Section */}
        <Box sx={{ mb: { xs: 1, md: 0.5 } }}>
          <Typography variant="subtitle2" sx={{ fontSize: '0.7rem', fontWeight: 600, mb: { xs: 0.5, md: 0.3 } }}>
            Sale Return Information
          </Typography>
          <Grid container spacing={{ xs: 0.75, md: 0.5 }}>
            <Grid item xs={12} sm={6} md={1.5}>
              <Typography variant="caption" sx={{ fontSize: '0.6rem', color: '#666', mb: 0.2, display: 'block' }}>
                Bill No *
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={formData.billNo}
                disabled
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontSize: '0.7rem',
                    bgcolor: '#f5f5f5',
                    height: '28px'
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={1}>
              <Typography variant="caption" sx={{ fontSize: '0.6rem', color: '#666', mb: 0.2, display: 'block' }}>
                Bill Date *
              </Typography>
              <TextField
                fullWidth
                type="date"
                size="small"
                value={formData.billDate}
                onChange={(e) => handleChange('billDate', e.target.value)}
                disabled={modalAction === 'view'}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontSize: '0.7rem',
                    bgcolor: 'white',
                    height: '28px'
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Typography variant="caption" sx={{ fontSize: '0.6rem', color: '#666', mb: 0.2, display: 'block' }}>
                customer *
              </Typography>
              {modalAction === 'view' ? (
                <TextField
                  fullWidth
                  size="small"
                  value={formData.customer || '-'}
                  disabled
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      fontSize: '0.7rem',
                      bgcolor: '#f5f5f5',
                      height: '28px'
                    }
                  }}
                />
              ) : (
                <TextField
                  fullWidth
                  select
                  size="small"
                  value={formData.customerId}
                  onChange={(e) => handleChange('customerId', e.target.value)}
                  disabled={modalAction === 'view'}
                  SelectProps={{
                    native: false,
                    MenuProps: {
                      PaperProps: {
                        style: {
                          maxHeight: 300
                        }
                      }
                    }
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      fontSize: '0.7rem',
                      bgcolor: 'white',
                      height: '28px'
                    }
                  }}
                >
                  <MenuItem value="" sx={{ fontSize: '0.7rem' }}>Select customer</MenuItem>
                  {customers.length > 0 ? (
                    customers.map((customer) => (
                      <MenuItem key={customer.MasterId || customer.Id} value={customer.MasterId || customer.Id} sx={{ fontSize: '0.7rem' }}>
                        {customer.Name}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem value="" disabled sx={{ fontSize: '0.7rem' }}>No customers available</MenuItem>
                  )}
                </TextField>
              )}
            </Grid>

            <Grid item xs={12} sm={6} md={1.8}>
              <Typography variant="caption" sx={{ fontSize: '0.6rem', color: '#666', mb: 0.2, display: 'block' }}>
                VAT No
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={formData.vatNo || '-'}
                onChange={(e) => handleChange('vatNo', e.target.value)}
                disabled={modalAction === 'view'}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontSize: '0.7rem',
                    bgcolor: modalAction === 'view' ? '#f5f5f5' : 'white',
                    height: '28px'
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Typography variant="caption" sx={{ fontSize: '0.6rem', color: '#666', mb: 0.2, display: 'block' }}>
                Sale No (Optional)
              </Typography>
              {modalAction === 'view' ? (
                <TextField
                  fullWidth
                  size="small"
                  value={formData.SaleNo || '-'}
                  disabled
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      fontSize: '0.7rem',
                      bgcolor: '#f5f5f5',
                      height: '28px'
                    }
                  }}
                />
              ) : (
                <TextField
                  fullWidth
                  select
                  size="small"
                  value={formData.SaleId}
                  onChange={(e) => handleChange('SaleId', e.target.value)}
                  disabled={!formData.customerId}
                  SelectProps={{
                    native: false,
                    MenuProps: {
                      PaperProps: {
                        style: {
                          maxHeight: 300
                        }
                      }
                    }
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      fontSize: '0.7rem',
                      bgcolor: formData.customerId ? 'white' : '#f5f5f5',
                      height: '28px'
                    }
                  }}
                >
                  <MenuItem value="" sx={{ fontSize: '0.7rem' }}>None</MenuItem>
                  {formData.customerId ? (
                    Sales.length > 0 ? (
                      Sales.map((Sale) => (
                        <MenuItem key={Sale.Id} value={Sale.Id} sx={{ fontSize: '0.7rem' }}>
                          {Sale.BillPrefix}/{Sale.BillNumber}/{Sale.FinYear}/{formatDate(Sale.BillDate)}
                        </MenuItem>
                      ))
                    ) : (
                      <MenuItem value="" disabled sx={{ fontSize: '0.7rem' }}>No Sales for this customer</MenuItem>
                    )
                  ) : (
                    <MenuItem value="" disabled sx={{ fontSize: '0.7rem' }}>Select customer first</MenuItem>
                  )}
                </TextField>
              )}
            </Grid>

            <Grid item xs={12} sm={6} md={1.8}>
              <Typography variant="caption" sx={{ fontSize: '0.6rem', color: '#666', mb: 0.2, display: 'block' }}>
                Salesman
              </Typography>
              {modalAction === 'view' ? (
                <TextField
                  fullWidth
                  size="small"
                  value={(() => {
                    const sid = parseInt(formData.SalesManId);
                    const s = staffList.find(x => (x.MasterId || x.Id) === sid);
                    return s ? (s.MasterName || s.Name || `${s.FirstName || ''} ${s.LastName || ''}`.trim()) : '-';
                  })()}
                  disabled
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      fontSize: '0.7rem',
                      bgcolor: '#f5f5f5',
                      height: '28px'
                    }
                  }}
                />
              ) : (
                <TextField
                  fullWidth
                  select
                  size="small"
                  value={formData.SalesManId}
                  onChange={(e) => handleChange('SalesManId', e.target.value)}
                  disabled={modalAction === 'view'}
                  SelectProps={{
                    native: false,
                    MenuProps: {
                      PaperProps: {
                        style: {
                          maxHeight: 300
                        }
                      }
                    }
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      fontSize: '0.7rem',
                      bgcolor: 'white',
                      height: '28px'
                    }
                  }}
                >
                  <MenuItem value="" sx={{ fontSize: '0.7rem' }}>All Salesmen</MenuItem>
                  {staffList.map((staff) => (
                    <MenuItem key={staff.MasterId || staff.Id} value={staff.MasterId || staff.Id} sx={{ fontSize: '0.7rem' }}>
                      {staff.MasterName || staff.Name || `${staff.FirstName || ''} ${staff.LastName || ''}`.trim()}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            </Grid>
            {/* <Grid item xs={12} sm={6} md={1.5}>
              <Typography variant="caption" sx={{ fontSize: '0.6rem', color: '#666', mb: 0.2, display: 'block' }}>
                VAT Type
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={formData.vatType}
                disabled
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontSize: '0.7rem',
                    bgcolor: '#f5f5f5',
                    height: '28px'
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={1.5}>
              <Typography variant="caption" sx={{ fontSize: '0.6rem', color: '#666', mb: 0.2, display: 'block' }}>
                Invoice Type
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={formData.invType}
                disabled
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontSize: '0.7rem',
                    bgcolor: '#f5f5f5',
                    height: '28px'
                  }
                }}
              />
            </Grid> */}
            <Grid item xs={12} sm={6} md={1.5}>
              <Typography variant="caption" sx={{ fontSize: '0.6rem', color: '#666', mb: 0.2, display: 'block' }}>
                Currency
              </Typography>
              {modalAction === 'view' ? (
                <TextField
                  fullWidth
                  size="small"
                  value={formData.CurrencyName || 'AED'}
                  disabled
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      fontSize: '0.7rem',
                      bgcolor: '#f5f5f5',
                      height: '28px'
                    }
                  }}
                />
              ) : (
                <Autocomplete
                  options={Array.isArray(currencies) ? currencies : []}
                  getOptionLabel={(option) => option.Currency || ''}
                  isOptionEqualToValue={(option, value) => option.Id === value?.Id}
                  value={Array.isArray(currencies) && formData.CurrencyTypeId ? currencies.find(c => c.Id === parseInt(formData.CurrencyTypeId)) || null : null}
                  onChange={(event, newValue) => {
                    if (newValue) {
                      setFormData(prev => ({
                        ...prev,
                        CurrencyTypeId: newValue.Id,
                        CurrencyName: newValue.Currency || ''
                      }));
                    } else {
                      setFormData(prev => ({
                        ...prev,
                        CurrencyTypeId: null,
                        CurrencyName: ''
                      }));
                    }
                  }}
                  disabled={currencyLoading}
                  loading={currencyLoading}
                  ListboxProps={{
                    style: { fontSize: '0.7rem', maxHeight: '200px' }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      placeholder="Select"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          fontSize: '0.7rem',
                          bgcolor: 'white',
                          height: '28px'
                        }
                      }}
                      InputProps={{
                        ...params.InputProps,
                        style: { fontSize: '0.7rem' },
                        endAdornment: (
                          <>
                            {currencyLoading ? <CircularProgress color="inherit" size={14} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        )
                      }}
                    />
                  )}
                  sx={{
                    '& .MuiInputBase-root': { minHeight: '28px', py: '0 !important' },
                    '& .MuiInputBase-input': { py: '0.4 !important', fontSize: '0.7rem' },
                    '& .MuiAutocomplete-input': { py: '3.5px !important' },
                    '& .MuiOutlinedInput-root': { minHeight: '28px', padding: '0 9px !important' },
                    '& .MuiAutocomplete-endAdornment': { top: '50%', transform: 'translateY(-50%)' }
                  }}
                />
              )}
            </Grid>
          </Grid>
        </Box>

        {/* Return Items Section */}
        <Box sx={{ mb: { xs: 1, md: 0.5 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: { xs: 0.5, md: 0.3 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#333' }}>
                Return Items {items.length > 0 && `(${items.length})`}
              </Typography>
              {items.length > 7 && (
                <Button
                  size="small"
                  onClick={() => setViewAllItemsModalOpen(true)}
                  sx={{
                    textTransform: 'none',
                    fontSize: '0.6rem',
                    color: '#1976d2',
                    textDecoration: 'underline',
                    minWidth: 'auto',
                    p: 0,
                    '&:hover': {
                      bgcolor: 'transparent',
                      textDecoration: 'underline',
                      color: '#1565c0'
                    }
                  }}
                >
                  View All ({items.length} items)
                </Button>
              )}
            </Box>
            {/* {modalAction !== 'view' && (
              <Button
                variant="contained"
                size="small"
                startIcon={<Add fontSize="small" />}
                onClick={handleAddItem}
                sx={{
                  textTransform: 'none',
                  fontSize: '0.6rem',
                  bgcolor: '#1976d2',
                  py: 0.3,
                  px: 0.75,
                  '&:hover': { bgcolor: '#1565c0' }
                }}
              >
                Add Item
              </Button>
            )} */}
          </Box>
          <TableContainer component={Paper} sx={{
            border: '1px solid #e0e0e0',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            maxHeight: '300px',
            overflow: 'auto',
            borderRadius: '6px'
          }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontSize: '0.6rem', fontWeight: 700, px: 0.4, py: 0.4, color: '#d97706', backgroundColor: '#fef3c7', border: '1px solid #fde68a', whiteSpace: 'nowrap' }}>#</TableCell>
                  <TableCell sx={{ fontSize: '0.6rem', fontWeight: 700, px: 0.4, py: 0.4, color: '#d97706', backgroundColor: '#fef3c7', minWidth: 180, border: '1px solid #fde68a', whiteSpace: 'nowrap' }}>Item</TableCell>
                  <TableCell sx={{ fontSize: '0.6rem', fontWeight: 700, px: 0.4, py: 0.4, color: '#d97706', backgroundColor: '#fef3c7', textAlign: 'center', border: '1px solid #fde68a', whiteSpace: 'nowrap' }}>HSN</TableCell>
                  {formData.SaleId && (
                    <>
                      <TableCell sx={{ fontSize: '0.6rem', fontWeight: 700, px: 0.4, py: 0.4, color: '#d97706', backgroundColor: '#fef3c7', textAlign: 'center', border: '1px solid #fde68a', whiteSpace: 'nowrap' }}>Sold Qty</TableCell>
                      <TableCell sx={{ fontSize: '0.6rem', fontWeight: 700, px: 0.4, py: 0.4, color: '#d97706', backgroundColor: '#fef3c7', textAlign: 'center', border: '1px solid #fde68a', whiteSpace: 'nowrap' }}>Returned</TableCell>
                      <TableCell sx={{ fontSize: '0.6rem', fontWeight: 700, px: 0.4, py: 0.4, color: '#d97706', backgroundColor: '#fef3c7', textAlign: 'center', border: '1px solid #fde68a', whiteSpace: 'nowrap' }}>Available</TableCell>
                    </>
                  )}
                  <TableCell sx={{ fontSize: '0.6rem', fontWeight: 700, px: 0.4, py: 0.4, color: '#d97706', backgroundColor: '#fef3c7', textAlign: 'center', border: '1px solid #fde68a', whiteSpace: 'nowrap' }}>Return Qty *</TableCell>
                  <TableCell sx={{ fontSize: '0.6rem', fontWeight: 700, px: 0.4, py: 0.4, color: '#d97706', backgroundColor: '#fef3c7', textAlign: 'right', border: '1px solid #fde68a', whiteSpace: 'nowrap' }}>Rate</TableCell>
                  <TableCell sx={{ fontSize: '0.6rem', fontWeight: 700, px: 0.4, py: 0.4, color: '#d97706', backgroundColor: '#fef3c7', textAlign: 'right', border: '1px solid #fde68a', whiteSpace: 'nowrap' }}>Amount</TableCell>
                  <TableCell sx={{ fontSize: '0.6rem', fontWeight: 700, px: 0.4, py: 0.4, color: '#d97706', backgroundColor: '#fef3c7', textAlign: 'right', border: '1px solid #fde68a', whiteSpace: 'nowrap' }}>Disc%</TableCell>
                  <TableCell sx={{ fontSize: '0.6rem', fontWeight: 700, px: 0.4, py: 0.4, color: '#d97706', backgroundColor: '#fef3c7', textAlign: 'right', border: '1px solid #fde68a', whiteSpace: 'nowrap' }}>Disc Amt</TableCell>
                  <TableCell sx={{ fontSize: '0.6rem', fontWeight: 700, px: 0.4, py: 0.4, color: '#d97706', backgroundColor: '#fef3c7', textAlign: 'center', border: '1px solid #fde68a', whiteSpace: 'nowrap' }}>VAT%</TableCell>
                  <TableCell sx={{ fontSize: '0.6rem', fontWeight: 700, px: 0.4, py: 0.4, color: '#d97706', backgroundColor: '#fef3c7', textAlign: 'right', border: '1px solid #fde68a', whiteSpace: 'nowrap' }}>Taxable</TableCell>
                  <TableCell sx={{ fontSize: '0.6rem', fontWeight: 700, px: 0.4, py: 0.4, color: '#d97706', backgroundColor: '#fef3c7', textAlign: 'right', border: '1px solid #fde68a', whiteSpace: 'nowrap' }}>
                    {isIGSTInvoice() ? 'IGST' : 'VAT Total'}
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.6rem', fontWeight: 700, px: 0.4, py: 0.4, color: '#d97706', backgroundColor: '#fef3c7', textAlign: 'right', border: '1px solid #fde68a', whiteSpace: 'nowrap' }}>Total</TableCell>
                  <TableCell sx={{ fontSize: '0.6rem', fontWeight: 700, px: 0.4, py: 0.4, color: '#d97706', backgroundColor: '#fef3c7', border: '1px solid #fde68a', width: '40px' }}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={14} sx={{ textAlign: 'center', py: 3, color: '#999', fontSize: '0.75rem' }}>
                      Select Item
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((item, index) => (
                    <TableRow key={item.id} sx={{ '&:hover': { bgcolor: '#f9fafb' } }}>
                      <TableCell sx={{ px: 0.4, py: 0.3, fontSize: '0.7rem', border: '1px solid #e5e7eb' }}>{index + 1}</TableCell>
                      <TableCell sx={{ px: 0.4, py: 0.3, border: '1px solid #e5e7eb' }}>
                        <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#6b21a8', px: 0.5 }}>
                          {item.item || itemsList.find(itm => itm.Id === parseInt(item.itemId))?.Name || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ px: 0.4, py: 0.3, textAlign: 'center', fontSize: '0.6rem', color: '#666', border: '1px solid #e5e7eb' }}>
                        {item.hsnCode || '-'}
                      </TableCell>
                      {formData.SaleId && (
                        <>
                          <TableCell sx={{ px: 0.4, py: 0.3, textAlign: 'center', fontSize: '0.7rem', fontWeight: 600, color: '#1976d2', border: '1px solid #e5e7eb' }}>
                            {item.SaledQty || 0}
                          </TableCell>
                          <TableCell sx={{ px: 0.4, py: 0.3, textAlign: 'center', fontSize: '0.7rem', fontWeight: 600, color: '#dc2626', border: '1px solid #e5e7eb' }}>
                            {item.alreadyReturnedQty || 0}
                          </TableCell>
                          <TableCell sx={{ px: 0.4, py: 0.3, textAlign: 'center', fontSize: '0.7rem', fontWeight: 600, color: '#059669', border: '1px solid #e5e7eb' }}>
                            {item.availableQty || 0}
                          </TableCell>
                        </>
                      )}
                      <TableCell sx={{ px: 0.4, py: 0.3, textAlign: 'center', border: '1px solid #e5e7eb' }}>
                        {formData.SaleId && (parseFloat(item.availableQty) || 0) <= 0 && (parseFloat(item.SaledQty) || 0) > 0 ? (
                          <Typography sx={{ fontSize: '0.6rem', color: '#dc2626', fontWeight: 600 }}>Fully Returned</Typography>
                        ) : (
                          <TextField
                            size="small"
                            type="number"
                            value={item.rcvdQty}
                            onChange={(e) => handleItemChange(item.id, 'rcvdQty', e.target.value)}
                            onFocus={(e) => e.target.select()}
                            disabled={modalAction === 'view'}
                            inputProps={{ min: 0, max: item.availableQty || item.SaledQty || 999999, step: 1, style: { textAlign: 'center' } }}
                            sx={{ width: '65px', '& .MuiOutlinedInput-root': { fontSize: '0.7rem', height: '26px' } }}
                          />
                        )}
                      </TableCell>
                      {formData.SaleId && (parseFloat(item.availableQty) || 0) <= 0 && (parseFloat(item.SaledQty) || 0) > 0 ? (
                        <>
                          <TableCell sx={{ px: 0.4, py: 0.3, textAlign: 'right', fontSize: '0.7rem', color: '#dc2626', border: '1px solid #e5e7eb' }}>{item.rate}</TableCell>
                          <TableCell sx={{ px: 0.4, py: 0.3, textAlign: 'right', fontSize: '0.7rem', color: '#dc2626', border: '1px solid #e5e7eb' }}>{currencySymbol} {(item.SaledQty * item.rate).toFixed(2)}</TableCell>
                          <TableCell sx={{ px: 0.4, py: 0.3, textAlign: 'right', fontSize: '0.7rem', color: '#dc2626', border: '1px solid #e5e7eb' }}>{item.discPercent}%</TableCell>
                          <TableCell sx={{ px: 0.4, py: 0.3, textAlign: 'right', fontSize: '0.7rem', color: '#dc2626', border: '1px solid #e5e7eb' }}>{currencySymbol} {(item.discAmt || 0).toFixed(2)}</TableCell>
                          <TableCell sx={{ px: 0.4, py: 0.3, textAlign: 'center', fontSize: '0.7rem', color: '#dc2626', border: '1px solid #e5e7eb' }}>{item.vatPercent}%</TableCell>
                          <TableCell sx={{ px: 0.4, py: 0.3, textAlign: 'right', fontSize: '0.7rem', color: '#dc2626', border: '1px solid #e5e7eb' }}>{currencySymbol} {(item.taxable || 0).toFixed(2)}</TableCell>
                          <TableCell sx={{ px: 0.4, py: 0.3, textAlign: 'right', fontSize: '0.7rem', color: '#dc2626', border: '1px solid #e5e7eb' }}>{currencySymbol} {((item.vatAmount || 0) + (item.igst || 0)).toFixed(2)}</TableCell>
                          <TableCell sx={{ px: 0.4, py: 0.3, textAlign: 'right', fontSize: '0.7rem', color: '#dc2626', border: '1px solid #e5e7eb' }}>{currencySymbol} {(item.total || 0).toFixed(2)}</TableCell>
                        </>
                      ) : (
                        <>
                      <TableCell sx={{ px: 0.4, py: 0.3, textAlign: 'right', fontSize: '0.7rem', border: '1px solid #e5e7eb' }}>
                        <TextField
                          size="small"
                          type="number"
                          value={item.rate}
                          onChange={(e) => handleItemChange(item.id, 'rate', e.target.value)}
                          onFocus={(e) => e.target.select()}
                          disabled={modalAction === 'view'}
                          inputProps={{ min: 0, step: 0.01, style: { textAlign: 'right' } }}
                          sx={{ width: '75px', '& .MuiOutlinedInput-root': { fontSize: '0.7rem', height: '26px' } }}
                        />
                      </TableCell>
                      <TableCell sx={{ px: 0.4, py: 0.3, textAlign: 'right', fontSize: '0.7rem', color: '#666', border: '1px solid #e5e7eb' }}>
                        {currencySymbol}  {(item.amount || 0).toFixed(2)}
                      </TableCell>
                      <TableCell sx={{ px: 0.4, py: 0.3, textAlign: 'right', border: '1px solid #e5e7eb' }}>
                        <TextField
                          size="small"
                          type="number"
                          value={item.discPercent}
                          onChange={(e) => handleItemChange(item.id, 'discPercent', e.target.value)}
                          onFocus={(e) => e.target.select()}
                          disabled={modalAction === 'view'}
                          inputProps={{ min: 0, max: 100, step: 0.01, style: { textAlign: 'right' } }}
                          sx={{ width: '55px', '& .MuiOutlinedInput-root': { fontSize: '0.7rem', height: '26px' } }}
                        />
                      </TableCell>
                      <TableCell sx={{ px: 0.4, py: 0.3, textAlign: 'right', border: '1px solid #e5e7eb' }}>
                        <TextField
                          size="small"
                          type="number"
                          value={item.discAmt}
                          onChange={(e) => handleItemChange(item.id, 'discAmt', e.target.value)}
                          onFocus={(e) => e.target.select()}
                          disabled={modalAction === 'view'}
                          inputProps={{ min: 0, step: 0.01, style: { textAlign: 'right' } }}
                          sx={{ width: '65px', '& .MuiOutlinedInput-root': { fontSize: '0.7rem', height: '26px' } }}
                        />
                      </TableCell>
                      <TableCell sx={{ px: 0.4, py: 0.3, textAlign: 'center', fontSize: '0.7rem', fontWeight: 600, color: '#059669', border: '1px solid #e5e7eb' }}>
                        {item.vatPercent}%
                      </TableCell>
                      <TableCell sx={{ px: 0.4, py: 0.3, textAlign: 'right', fontSize: '0.7rem', border: '1px solid #e5e7eb' }}>
                        {currencySymbol} {(item.taxable || 0).toFixed(2)}
                      </TableCell>
                      <TableCell sx={{ px: 0.4, py: 0.3, textAlign: 'right', fontSize: '0.7rem', border: '1px solid #e5e7eb' }}>
                        {currencySymbol}  {((item.vatAmount || 0) + (item.igst || 0)).toFixed(2)}
                      </TableCell>
                      <TableCell sx={{ px: 0.4, py: 0.3, textAlign: 'right', fontSize: '0.7rem', fontWeight: 600, border: '1px solid #e5e7eb' }}>
                        {currencySymbol}  {(item.total || 0).toFixed(2)}
                      </TableCell>
                        </>
                      )}
                      <TableCell sx={{ px: 0.4, py: 0.3, border: '1px solid #e5e7eb' }}>
                        {modalAction !== 'view' && !(formData.SaleId && (parseFloat(item.availableQty) || 0) <= 0 && (parseFloat(item.SaledQty) || 0) > 0) && (
                          <IconButton size="small" onClick={() => handleDeleteItem(item.id)} color="error" sx={{ p: 0.2 }}>
                            <Delete fontSize="small" />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
                {/* Totals Row */}
                {items.length > 0 && (
                  <TableRow sx={{ bgcolor: '#f0f9ff', borderTop: '2px solid #1976d2' }}>
                    <TableCell colSpan={3} sx={{ px: 0.4, py: 0.4, fontSize: '0.7rem', fontWeight: 700, border: '1px solid #e5e7eb', textAlign: 'right' }}>
                      Total:
                    </TableCell>
                    {formData.SaleId && (
                      <>
                        <TableCell sx={{ px: 0.4, py: 0.4, textAlign: 'center', fontSize: '0.7rem', fontWeight: 700, color: '#1976d2', border: '1px solid #e5e7eb' }}>
                          {items.reduce((sum, item) => sum + (parseFloat(item.SaledQty) || 0), 0)}
                        </TableCell>
                        <TableCell sx={{ px: 0.4, py: 0.4, textAlign: 'center', fontSize: '0.7rem', fontWeight: 700, color: '#dc2626', border: '1px solid #e5e7eb' }}>
                          {items.reduce((sum, item) => sum + (parseFloat(item.alreadyReturnedQty) || 0), 0)}
                        </TableCell>
                        <TableCell sx={{ px: 0.4, py: 0.4, textAlign: 'center', fontSize: '0.7rem', fontWeight: 700, color: '#059669', border: '1px solid #e5e7eb' }}>
                          {items.reduce((sum, item) => sum + (parseFloat(item.availableQty) || 0), 0)}
                        </TableCell>
                      </>
                    )}
                    <TableCell sx={{ px: 0.4, py: 0.4, textAlign: 'center', fontSize: '0.7rem', fontWeight: 700, color: '#059669', border: '1px solid #e5e7eb' }}>
                      {items.reduce((sum, item) => sum + (parseFloat(item.rcvdQty) || 0), 0)}
                    </TableCell>
                    <TableCell sx={{ px: 0.4, py: 0.4, border: '1px solid #e5e7eb' }}></TableCell>
                    <TableCell sx={{ px: 0.4, py: 0.4, textAlign: 'right', fontSize: '0.7rem', fontWeight: 700, border: '1px solid #e5e7eb' }}>
                      {currencySymbol}  {items.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0).toFixed(2)}
                    </TableCell>
                    <TableCell sx={{ px: 0.4, py: 0.4, border: '1px solid #e5e7eb' }}></TableCell>
                    <TableCell sx={{ px: 0.4, py: 0.4, textAlign: 'right', fontSize: '0.7rem', fontWeight: 700, border: '1px solid #e5e7eb' }}>
                      {currencySymbol}  {items.reduce((sum, item) => sum + (parseFloat(item.discAmt) || 0), 0).toFixed(2)}
                    </TableCell>
                    <TableCell sx={{ px: 0.4, py: 0.4, border: '1px solid #e5e7eb' }}></TableCell>
                    <TableCell sx={{ px: 0.4, py: 0.4, textAlign: 'right', fontSize: '0.7rem', fontWeight: 700, border: '1px solid #e5e7eb' }}>
                      {currencySymbol}  {items.reduce((sum, item) => sum + (parseFloat(item.taxable) || 0), 0).toFixed(2)}
                    </TableCell>
                    <TableCell sx={{ px: 0.4, py: 0.4, textAlign: 'right', fontSize: '0.7rem', fontWeight: 700, border: '1px solid #e5e7eb' }}>
                      {currencySymbol}  {items.reduce((sum, item) => sum + ((parseFloat(item.vatAmount) || 0) + (parseFloat(item.igst) || 0)), 0).toFixed(2)}
                    </TableCell>
                    <TableCell sx={{ px: 0.4, py: 0.4, textAlign: 'right', fontSize: '0.7rem', fontWeight: 700, color: '#1976d2', border: '1px solid #e5e7eb' }}>
                      {currencySymbol}  {items.reduce((sum, item) => sum + (parseFloat(item.total) || 0), 0).toFixed(2)}
                    </TableCell>
                    <TableCell sx={{ px: 0.4, py: 0.4, border: '1px solid #e5e7eb' }}></TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* Return Summary Section */}
        <Box sx={{ mb: { xs: 1, md: 0.5 } }}>
          <Typography variant="subtitle2" sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#333', mb: { xs: 0.5, md: 0.3 } }}>
            Return Summary
          </Typography>
          <Grid container spacing={{ xs: 1, md: 0.5 }}>
            {/* Additional Charges */}
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" sx={{ fontSize: '0.65rem', fontWeight: 600, color: '#666', mb: { xs: 0.5, md: 0.3 } }}>
                Additional Charges
              </Typography>
              <Box sx={{ bgcolor: '#fafafa', p: { xs: 1, md: 0.75 }, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                <Grid container spacing={{ xs: 0.75, md: 0.5 }}>
                  <Grid item xs={4}>
                    <Typography variant="caption" sx={{ fontSize: '0.55rem', color: '#888', mb: 0.2, display: 'block', textTransform: 'uppercase' }}>
                      Freight Amount
                    </Typography>
                    <Typography variant="caption" sx={{ fontSize: '0.5rem', color: '#059669', mb: 0.2, display: 'block' }}>
                      {isIGSTInvoice() ? 'IGST' : 'VAT'} Rate: {summary.effectiveVATRate}%
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      value={additionalCharges.freight}
                      onChange={(e) => handleAdditionalChargeChange('freight', e.target.value)}
                      onFocus={(e) => e.target.select()}
                      disabled={modalAction === 'view'}
                      inputProps={{ min: 0, step: 0.01, style: { textAlign: 'right' } }}
                      sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.7rem', bgcolor: 'white', height: '28px' } }}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="caption" sx={{ fontSize: '0.55rem', color: '#888', mb: 0.2, display: 'block', textTransform: 'uppercase' }}>
                      Packing Amount
                    </Typography>
                    <Typography variant="caption" sx={{ fontSize: '0.5rem', color: '#059669', mb: 0.2, display: 'block' }}>
                      {isIGSTInvoice() ? 'IGST' : 'VAT'} Rate: {summary.effectiveVATRate}%
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      value={additionalCharges.packing}
                      onChange={(e) => handleAdditionalChargeChange('packing', e.target.value)}
                      onFocus={(e) => e.target.select()}
                      disabled={modalAction === 'view'}
                      inputProps={{ min: 0, step: 0.01, style: { textAlign: 'right' } }}
                      sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.7rem', bgcolor: 'white', height: '28px' } }}
                    />
                  </Grid>
                  {/* <Grid item xs={6}>
                    <Typography variant="caption" sx={{ fontSize: '0.55rem', color: '#888', mb: 0.2, display: 'block', textTransform: 'uppercase' }}>
                      TCS Amount
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      value={additionalCharges.tcsAmount}
                      onChange={(e) => handleAdditionalChargeChange('tcsAmount', e.target.value)}
                      onFocus={(e) => e.target.select()}
                      inputProps={{ min: 0, step: 0.01, style: { textAlign: 'right' } }}
                      sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.7rem', bgcolor: 'white', height: '28px' } }}
                    />
                  </Grid> */}
                  <Grid item xs={4}>
                    <Typography variant="caption" sx={{ fontSize: '0.55rem', color: '#888', mb: 0.2, display: 'block', textTransform: 'uppercase' }}>
                      Total Amount
                    </Typography>
                    <Typography variant="caption" sx={{ fontSize: '0.5rem', color: '#059669', mb: 0.2, display: 'block' }}>
                      -
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      value={summary.total}
                      disabled
                      inputProps={{ style: { textAlign: 'right' } }}
                      sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.7rem', bgcolor: '#f5f5f5', height: '28px' } }}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="caption" sx={{ fontSize: '0.55rem', color: '#888', mb: 0.2, display: 'block', textTransform: 'uppercase' }}>
                      Cash Amount
                    </Typography>
                    <Typography variant="caption" sx={{ fontSize: '0.5rem', color: '#059669', mb: 0.2, display: 'block' }}>
                      -
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      value={additionalCharges.cashAmount}
                      onChange={(e) => handleAdditionalChargeChange('cashAmount', e.target.value)}
                      onFocus={(e) => e.target.select()}
                      disabled={modalAction === 'view'}
                      inputProps={{ min: 0, step: 0.01, style: { textAlign: 'right' } }}
                      sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.7rem', bgcolor: 'white', height: '28px' } }}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="caption" sx={{ fontSize: '0.55rem', color: '#888', mb: 0.2, display: 'block', textTransform: 'uppercase' }}>
                      UPI Amount
                    </Typography>
                    <Typography variant="caption" sx={{ fontSize: '0.5rem', color: '#059669', mb: 0.2, display: 'block' }}>
                      -
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      value={additionalCharges.upiAmount}
                      onChange={(e) => handleAdditionalChargeChange('upiAmount', e.target.value)}
                      onFocus={(e) => e.target.select()}
                      disabled={modalAction === 'view'}
                      inputProps={{ min: 0, step: 0.01, style: { textAlign: 'right' } }}
                      sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.7rem', bgcolor: 'white', height: '28px' } }}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="caption" sx={{ fontSize: '0.55rem', color: '#888', mb: 0.2, display: 'block', textTransform: 'uppercase' }}>
                      Card Amount
                    </Typography>
                    <Typography variant="caption" sx={{ fontSize: '0.5rem', color: '#059669', mb: 0.2, display: 'block' }}>
                      -
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      value={additionalCharges.cardAmount}
                      onChange={(e) => handleAdditionalChargeChange('cardAmount', e.target.value)}
                      onFocus={(e) => e.target.select()}
                      disabled={modalAction === 'view'}
                      inputProps={{ min: 0, step: 0.01, style: { textAlign: 'right' } }}
                      sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.7rem', bgcolor: 'white', height: '28px' } }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" sx={{ fontSize: '0.55rem', color: '#888', mb: 0.2, display: 'block', textTransform: 'uppercase' }}>
                      Remarks
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      type='text'
                      value={additionalCharges.remarks}
                      onChange={(e) => handleAdditionalChargeChange('remarks', e.target.value)}
                      disabled={modalAction === 'view'}
                      placeholder="Enter remarks..."
                      sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.7rem', bgcolor: 'white', height: '28px' } }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    {((parseFloat(additionalCharges.upiAmount) || 0) > 0 || (parseFloat(additionalCharges.cardAmount) || 0) > 0) ? (
                      <Box>
                        <Typography variant="caption" sx={{ fontSize: '0.55rem', color: '#888', mb: 0.2, display: 'block', textTransform: 'uppercase' }}>
                          Bank *
                        </Typography>
                        <TextField
                          fullWidth
                          select
                          size="small"
                          value={additionalCharges.bankId}
                          onChange={(e) => handleAdditionalChargeChange('bankId', e.target.value)}
                          disabled={modalAction === 'view'}
                          SelectProps={{
                            native: false,
                            MenuProps: {
                              PaperProps: {
                                style: {
                                  maxHeight: 200
                                }
                              }
                            }
                          }}
                          sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.7rem', bgcolor: 'white', height: '28px' } }}
                        >
                          <MenuItem value="" sx={{ fontSize: '0.7rem' }}>Select Bank</MenuItem>
                          {banks.length > 0 ? (
                            banks.map((bank) => (
                              <MenuItem key={bank.MasterId || bank.Id} value={bank.MasterId || bank.Id} sx={{ fontSize: '0.7rem' }}>
                                {bank.Name || bank.MasterName}
                              </MenuItem>
                            ))
                          ) : (
                            <MenuItem value="" disabled sx={{ fontSize: '0.7rem' }}>No banks available</MenuItem>
                          )}
                        </TextField>
                      </Box>
                    ) : (
                      <Box sx={{
                        height: '28px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: '#f9fafb',
                        borderRadius: 1,
                        border: '1px dashed #d1d5db'
                      }}>
                        <Typography variant="caption" sx={{
                          color: '#9ca3af',
                          fontSize: '0.6rem',
                          fontStyle: 'italic'
                        }}>
                          Bank field appears for UPI/Card
                        </Typography>
                      </Box>
                    )}
                  </Grid>
                </Grid>
              </Box>
            </Grid>

            {/* Return Calculation */}
            <Grid item xs={12} md={8}>
              <Typography variant="subtitle2" sx={{ fontSize: '0.65rem', fontWeight: 600, color: '#666', mb: { xs: 0.5, md: 0.3 } }}>
                Return Calculation
              </Typography>
              <Box sx={{ bgcolor: '#fafafa', p: { xs: 0.75, md: 0.5 }, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                <Grid container spacing={{ xs: 0.5, md: 0.3 }}>
                  <Grid item xs={6} md={2.4}>
                    <Box sx={{ textAlign: 'center', mb: 0.2 }}>
                      <Typography sx={{ fontSize: '0.5rem', color: '#888', mb: 0.1, textTransform: 'uppercase' }}>Subtotal</Typography>
                      <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#333' }}>{currencySymbol} {summary.subtotal.toFixed(2)}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} md={2.4}>
                    <Box sx={{ textAlign: 'center', mb: 0.2 }}>
                      <Typography sx={{ fontSize: '0.5rem', color: '#888', mb: 0.1, textTransform: 'uppercase' }}>Discount</Typography>
                      <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#d32f2f' }}>{currencySymbol} {summary.discount.toFixed(2)}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} md={2.4}>
                    <Box sx={{ textAlign: 'center', mb: 0.2 }}>
                      <Typography sx={{ fontSize: '0.5rem', color: '#888', mb: 0.1, textTransform: 'uppercase' }}>Taxable</Typography>
                      <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#333' }}>{currencySymbol} {summary.taxable.toFixed(2)}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} md={2.4}>
                    <Box sx={{ textAlign: 'center', mb: 0.2 }}>
                      <Typography sx={{ fontSize: '0.5rem', color: '#888', mb: 0.1, textTransform: 'uppercase' }}>
                        {isIGSTInvoice() ? 'IGST' : 'VAT Total'}
                      </Typography>
                      <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#059669' }}>
                        {currencySymbol}  {(summary.vatamount + summary.igst).toFixed(2)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} md={2.4}>
                    <Box sx={{ textAlign: 'center', mb: 0.2 }}>
                      <Typography sx={{ fontSize: '0.5rem', color: '#888', mb: 0.1, textTransform: 'uppercase' }}>Freight</Typography>
                      <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#333' }}>{currencySymbol} {(additionalCharges.freight || 0).toFixed(2)}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} md={2.4}>
                    <Box sx={{ textAlign: 'center', mb: 0.2 }}>
                      <Typography sx={{ fontSize: '0.5rem', color: '#888', mb: 0.1, textTransform: 'uppercase' }}>
                        {isIGSTInvoice() ? 'Freight IGST' : 'Freight VAT'}
                      </Typography>
                      <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#333' }}>{currencySymbol} {(summary.freightVAT || 0).toFixed(2)}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} md={2.4}>
                    <Box sx={{ textAlign: 'center', mb: 0.2 }}>
                      <Typography sx={{ fontSize: '0.5rem', color: '#888', mb: 0.1, textTransform: 'uppercase' }}>Packing</Typography>
                      <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#333' }}>{currencySymbol} {(additionalCharges.packing || 0).toFixed(2)}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} md={2.4}>
                    <Box sx={{ textAlign: 'center', mb: 0.2 }}>
                      <Typography sx={{ fontSize: '0.5rem', color: '#888', mb: 0.1, textTransform: 'uppercase' }}>
                        {isIGSTInvoice() ? 'Packing IGST' : 'Packing VAT'}
                      </Typography>
                      <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#333' }}>{currencySymbol} {(summary.packingVAT || 0).toFixed(2)}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} md={2.4}>
                    <Box sx={{ textAlign: 'center', mb: 0.2 }}>
                      <Typography sx={{ fontSize: '0.5rem', color: '#888', mb: 0.1, textTransform: 'uppercase' }}>Round Off</Typography>
                      <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#333' }}>
                        {summary.roundOff >= 0 ? '+' : ''} {currencySymbol} {summary.roundOff.toFixed(2)}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>

                <Grid container spacing={0.5} sx={{ mt: 0.2 }}>
                  <Grid item xs={6}>
                    <Box sx={{ bgcolor: '#fff3e0', p: 0.5, borderRadius: 1, border: '1.5px solid #ffb74d', textAlign: 'center' }}>
                      <Typography sx={{ fontSize: '0.5rem', color: '#e65100', mb: 0.1, fontWeight: 600 }}>Return Total Amount</Typography>
                      <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#e65100' }}>{currencySymbol}  {summary.total.toFixed(2)}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{ bgcolor: '#e3f2fd', p: 0.5, borderRadius: 1, border: '1.5px solid #64b5f6', textAlign: 'center' }}>
                      <Typography sx={{ fontSize: '0.5rem', color: '#1565c0', mb: 0.1, fontWeight: 600 }}>Balance Amount</Typography>
                      <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#1565c0' }}>
                        {currencySymbol}  {summary.balance.toFixed(2)}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions sx={{
        p: 0.75,
        borderTop: '1px solid #e0e0e0',
        bgcolor: '#f5f5f5',
        gap: 0.5
      }}>
        <Button
          variant="outlined"
          onClick={onClose}
          sx={{
            textTransform: 'none',
            fontSize: '0.7rem',
            py: 0.4,
            px: 1.25,
            borderColor: '#999',
            color: '#666',
            '&:hover': {
              borderColor: '#666',
              bgcolor: '#f5f5f5'
            }
          }}
        >
          {modalAction === 'view' ? 'Close' : 'Cancel'}
        </Button>
        {modalAction !== 'view' && (
          <Button
            variant="contained"
            onClick={handleSubmit}
            sx={{
              textTransform: 'none',
              fontSize: '0.7rem',
              py: 0.4,
              px: 1.25,
              bgcolor: '#333',
              '&:hover': {
                bgcolor: '#555'
              }
            }}
          >
            Save Changes
          </Button>
        )}
      </DialogActions>

      {/* View All Items Modal */}
      <Dialog
        open={viewAllItemsModalOpen}
        onClose={() => setViewAllItemsModalOpen(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle sx={{
          bgcolor: '#f5f5f5',
          borderBottom: '1px solid #e0e0e0',
          p: 1.5,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600, color: '#333' }}>
            All Return Items ({items.length})
          </Typography>
          <IconButton onClick={() => setViewAllItemsModalOpen(false)} size="small" sx={{ p: 0.5 }}>
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 2, bgcolor: '#fafafa' }}>
          <TableContainer component={Paper} sx={{ border: '1px solid #ddd', boxShadow: 'none', maxHeight: '500px', overflow: 'auto' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow sx={{ bgcolor: '#ffe4cc', height: '32px' }}>
                  <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600, p: 0.1, py: 0.3, color: '#d97706', backgroundColor: '#f7e0c7ff', border: '1px solid #ffd4a3', height: '32px' }}>#</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600, p: 0.1, py: 0.3, color: '#d97706', backgroundColor: '#f7e0c7ff', minWidth: 200, border: '1px solid #ffd4a3', height: '32px' }}>Item</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600, p: 0.1, py: 0.3, color: '#d97706', backgroundColor: '#f7e0c7ff', textAlign: 'center', border: '1px solid #ffd4a3', height: '32px' }}>HSN</TableCell>
                  {formData.SaleId && (
                    <>
                      <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600, p: 0.1, py: 0.3, color: '#d97706', backgroundColor: '#f7e0c7ff', textAlign: 'center', border: '1px solid #ffd4a3', height: '32px' }}>Sold Qty</TableCell>
                      <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600, p: 0.1, py: 0.3, color: '#d97706', backgroundColor: '#f7e0c7ff', textAlign: 'center', border: '1px solid #ffd4a3', height: '32px' }}>Returned</TableCell>
                      <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600, p: 0.1, py: 0.3, color: '#d97706', backgroundColor: '#f7e0c7ff', textAlign: 'center', border: '1px solid #ffd4a3', height: '32px' }}>Available</TableCell>
                    </>
                  )}
                  <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600, p: 0.1, py: 0.3, color: '#d97706', backgroundColor: '#f7e0c7ff', textAlign: 'center', border: '1px solid #ffd4a3', height: '32px' }}>Return Qty</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600, p: 0.1, py: 0.3, color: '#d97706', backgroundColor: '#f7e0c7ff', textAlign: 'right', border: '1px solid #ffd4a3', height: '32px' }}>Rate</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600, p: 0.1, py: 0.3, color: '#d97706', backgroundColor: '#f7e0c7ff', textAlign: 'right', border: '1px solid #ffd4a3', height: '32px' }}>Amount</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600, p: 0.1, py: 0.3, color: '#d97706', backgroundColor: '#f7e0c7ff', textAlign: 'right', border: '1px solid #ffd4a3', height: '32px' }}>Disc%</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600, p: 0.1, py: 0.3, color: '#d97706', backgroundColor: '#f7e0c7ff', textAlign: 'right', border: '1px solid #ffd4a3', height: '32px' }}>Disc Amt</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600, p: 0.1, py: 0.3, color: '#d97706', backgroundColor: '#f7e0c7ff', textAlign: 'center', border: '1px solid #ffd4a3', height: '32px' }}>VAT%</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600, p: 0.1, py: 0.3, color: '#d97706', backgroundColor: '#f7e0c7ff', textAlign: 'right', border: '1px solid #ffd4a3', height: '32px' }}>Taxable</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600, p: 0.1, py: 0.3, color: '#d97706', backgroundColor: '#f7e0c7ff', textAlign: 'right', border: '1px solid #ffd4a3', height: '32px' }}>
                    {isIGSTInvoice() ? 'IGST' : 'VAT Total'}
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600, p: 0.1, py: 0.3, color: '#d97706', backgroundColor: '#f7e0c7ff', textAlign: 'right', border: '1px solid #ffd4a3', height: '32px' }}>Total</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600, p: 0.1, py: 0.3, color: '#d97706', backgroundColor: '#f7e0c7ff', border: '1px solid #ffd4a3', height: '32px' }}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item, index) => (
                  <TableRow key={item.id} sx={{ '&:hover': { bgcolor: '#f9fafb' }, height: '40px' }}>
                    <TableCell sx={{ p: 0.1, py: 0.3, fontSize: '0.875rem', border: '1px solid #e5e7eb', height: '40px' }}>{index + 1}</TableCell>
                    <TableCell sx={{ p: 0.1, py: 0.3, border: '1px solid #e5e7eb', height: '40px' }}>
                      <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: '#6b21a8', px: 0.5 }}>
                        {item.item || itemsList.find(itm => itm.Id === parseInt(item.itemId))?.Name || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ p: 0.1, py: 0.3, textAlign: 'center', fontSize: '0.75rem', color: '#666', border: '1px solid #e5e7eb', height: '40px' }}>
                      {item.hsnCode || '-'}
                    </TableCell>
                    {formData.SaleId && (
                      <>
                        <TableCell sx={{ p: 0.1, py: 0.3, textAlign: 'center', fontSize: '0.875rem', fontWeight: 600, color: '#1976d2', border: '1px solid #e5e7eb', height: '40px' }}>
                          {item.SaledQty || 0}
                        </TableCell>
                        <TableCell sx={{ p: 0.1, py: 0.3, textAlign: 'center', fontSize: '0.875rem', fontWeight: 600, color: '#dc2626', border: '1px solid #e5e7eb', height: '40px' }}>
                          {item.alreadyReturnedQty || 0}
                        </TableCell>
                        <TableCell sx={{ p: 0.1, py: 0.3, textAlign: 'center', fontSize: '0.875rem', fontWeight: 600, color: '#059669', border: '1px solid #e5e7eb', height: '40px' }}>
                          {item.availableQty || 0}
                        </TableCell>
                      </>
                    )}
                    <TableCell sx={{ p: 0.1, py: 0.3, textAlign: 'center', border: '1px solid #e5e7eb', height: '40px' }}>
                      {formData.SaleId && (parseFloat(item.availableQty) || 0) <= 0 && (parseFloat(item.SaledQty) || 0) > 0 ? (
                        <Typography sx={{ fontSize: '0.75rem', color: '#dc2626', fontWeight: 600 }}>Fully Returned</Typography>
                      ) : (
                        <TextField
                          size="small"
                          type="number"
                          value={item.rcvdQty}
                          onChange={(e) => handleItemChange(item.id, 'rcvdQty', e.target.value)}
                          onFocus={(e) => e.target.select()}
                          disabled={modalAction === 'view'}
                          inputProps={{ min: 0, max: item.availableQty || item.SaledQty || 999999, step: 1, style: { textAlign: 'center' } }}
                          sx={{ width: '80px', '& .MuiOutlinedInput-root': { fontSize: '0.875rem', height: '32px' } }}
                        />
                      )}
                    </TableCell>
                    {formData.SaleId && (parseFloat(item.availableQty) || 0) <= 0 && (parseFloat(item.SaledQty) || 0) > 0 ? (
                      <>
                        <TableCell sx={{ p: 0.1, py: 0.3, textAlign: 'right', fontSize: '0.875rem', color: '#dc2626', border: '1px solid #e5e7eb', height: '40px' }}>{item.rate}</TableCell>
                        <TableCell sx={{ p: 0.1, py: 0.3, textAlign: 'right', fontSize: '0.875rem', color: '#dc2626', border: '1px solid #e5e7eb', height: '40px' }}>{currencySymbol} {(item.SaledQty * item.rate).toFixed(2)}</TableCell>
                        <TableCell sx={{ p: 0.1, py: 0.3, textAlign: 'right', fontSize: '0.875rem', color: '#dc2626', border: '1px solid #e5e7eb', height: '40px' }}>{item.discPercent}%</TableCell>
                        <TableCell sx={{ p: 0.1, py: 0.3, textAlign: 'right', fontSize: '0.875rem', color: '#dc2626', border: '1px solid #e5e7eb', height: '40px' }}>{currencySymbol} {(item.discAmt || 0).toFixed(2)}</TableCell>
                        <TableCell sx={{ p: 0.1, py: 0.3, textAlign: 'center', fontSize: '0.875rem', color: '#dc2626', border: '1px solid #e5e7eb', height: '40px' }}>{item.vatPercent}%</TableCell>
                        <TableCell sx={{ p: 0.1, py: 0.3, textAlign: 'right', fontSize: '0.875rem', color: '#dc2626', border: '1px solid #e5e7eb', height: '40px' }}>{currencySymbol} {(item.taxable || 0).toFixed(2)}</TableCell>
                        <TableCell sx={{ p: 0.1, py: 0.3, textAlign: 'right', fontSize: '0.875rem', color: '#dc2626', border: '1px solid #e5e7eb', height: '40px' }}>{currencySymbol} {((item.vatamount || 0) + (item.igst || 0)).toFixed(2)}</TableCell>
                        <TableCell sx={{ p: 0.1, py: 0.3, textAlign: 'right', fontSize: '0.875rem', color: '#dc2626', border: '1px solid #e5e7eb', height: '40px' }}>{currencySymbol} {(item.total || 0).toFixed(2)}</TableCell>
                      </>
                    ) : (
                      <>
                    <TableCell sx={{ p: 0.1, py: 0.3, textAlign: 'right', fontSize: '0.875rem', border: '1px solid #e5e7eb', height: '40px' }}>
                      <TextField
                        size="small"
                        type="number"
                        value={item.rate}
                        onChange={(e) => handleItemChange(item.id, 'rate', e.target.value)}
                        onFocus={(e) => e.target.select()}
                        disabled={modalAction === 'view'}
                        inputProps={{ min: 0, step: 0.01, style: { textAlign: 'right' } }}
                        sx={{ width: '90px', '& .MuiOutlinedInput-root': { fontSize: '0.875rem', height: '32px' } }}
                      />
                    </TableCell>
                    <TableCell sx={{ p: 0.1, py: 0.3, textAlign: 'right', fontSize: '0.875rem', color: '#666', border: '1px solid #e5e7eb', height: '40px' }}>
                      {currencySymbol}  {(item.amount || 0).toFixed(2)}
                    </TableCell>
                    <TableCell sx={{ p: 0.1, py: 0.3, textAlign: 'right', border: '1px solid #e5e7eb', height: '40px' }}>
                      <TextField
                        size="small"
                        type="number"
                        value={item.discPercent}
                        onChange={(e) => handleItemChange(item.id, 'discPercent', e.target.value)}
                        onFocus={(e) => e.target.select()}
                        disabled={modalAction === 'view'}
                        inputProps={{ min: 0, max: 100, step: 0.01, style: { textAlign: 'right' } }}
                        sx={{ width: '70px', '& .MuiOutlinedInput-root': { fontSize: '0.875rem', height: '32px' } }}
                      />
                    </TableCell>
                    <TableCell sx={{ p: 0.1, py: 0.3, textAlign: 'right', border: '1px solid #e5e7eb', height: '40px' }}>
                      <TextField
                        size="small"
                        type="number"
                        value={item.discAmt}
                        onChange={(e) => handleItemChange(item.id, 'discAmt', e.target.value)}
                        onFocus={(e) => e.target.select()}
                        disabled={modalAction === 'view'}
                        inputProps={{ min: 0, step: 0.01, style: { textAlign: 'right' } }}
                        sx={{ width: '80px', '& .MuiOutlinedInput-root': { fontSize: '0.875rem', height: '32px' } }}
                      />
                    </TableCell>
                    <TableCell sx={{ p: 0.1, py: 0.3, textAlign: 'center', fontSize: '0.875rem', fontWeight: 600, color: '#059669', border: '1px solid #e5e7eb', height: '40px' }}>
                      {item.vatPercent}%
                    </TableCell>
                    <TableCell sx={{ p: 0.1, py: 0.3, textAlign: 'right', fontSize: '0.875rem', border: '1px solid #e5e7eb', height: '40px' }}>
                      {currencySymbol}  {(item.taxable || 0).toFixed(2)}
                    </TableCell>
                    <TableCell sx={{ p: 0.1, py: 0.3, textAlign: 'right', fontSize: '0.875rem', border: '1px solid #e5e7eb', height: '40px' }}>
                      {currencySymbol}  {((item.vatamount || 0) + (item.igst || 0)).toFixed(2)}
                    </TableCell>
                    <TableCell sx={{ p: 0.1, py: 0.3, textAlign: 'right', fontSize: '0.875rem', fontWeight: 600, border: '1px solid #e5e7eb', height: '40px' }}>
                      {currencySymbol}  {(item.total || 0).toFixed(2)}
                    </TableCell>
                      </>
                    )}

                    <TableCell sx={{ p: 0.1, py: 0.3, border: '1px solid #e5e7eb', height: '40px' }}>
                      {modalAction !== 'view' && !(formData.SaleId && (parseFloat(item.availableQty) || 0) <= 0 && (parseFloat(item.SaledQty) || 0) > 0) && (
                        <IconButton size="small" onClick={() => handleDeleteItem(item.id)} color="error" sx={{ p: 0.1 }}>
                          <Delete fontSize="small" />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {/* Totals Row */}
                {items.length > 0 && (
                  <TableRow sx={{ bgcolor: '#f0f9ff', borderTop: '2px solid #1976d2' }}>
                    <TableCell colSpan={3} sx={{ p: 0.1, py: 0.3, fontSize: '0.875rem', fontWeight: 700, border: '1px solid #e5e7eb', textAlign: 'right' }}>
                      Total:
                    </TableCell>
                    {formData.SaleId && (
                      <>
                        <TableCell sx={{ p: 0.1, py: 0.3, textAlign: 'center', fontSize: '0.875rem', fontWeight: 700, color: '#1976d2', border: '1px solid #e5e7eb' }}>
                          {items.reduce((sum, item) => sum + (parseFloat(item.SaledQty) || 0), 0)}
                        </TableCell>
                        <TableCell sx={{ p: 0.1, py: 0.3, textAlign: 'center', fontSize: '0.875rem', fontWeight: 700, color: '#dc2626', border: '1px solid #e5e7eb' }}>
                          {items.reduce((sum, item) => sum + (parseFloat(item.alreadyReturnedQty) || 0), 0)}
                        </TableCell>
                        <TableCell sx={{ p: 0.1, py: 0.3, textAlign: 'center', fontSize: '0.875rem', fontWeight: 700, color: '#059669', border: '1px solid #e5e7eb' }}>
                          {items.reduce((sum, item) => sum + (parseFloat(item.availableQty) || 0), 0)}
                        </TableCell>
                      </>
                    )}
                    <TableCell sx={{ p: 0.1, py: 0.3, textAlign: 'center', fontSize: '0.875rem', fontWeight: 700, color: '#059669', border: '1px solid #e5e7eb' }}>
                      {items.reduce((sum, item) => sum + (parseFloat(item.rcvdQty) || 0), 0)}
                    </TableCell>
                    <TableCell sx={{ p: 0.1, py: 0.3, border: '1px solid #e5e7eb' }}></TableCell>
                    <TableCell sx={{ p: 0.1, py: 0.3, textAlign: 'right', fontSize: '0.875rem', fontWeight: 700, border: '1px solid #e5e7eb' }}>
                      {currencySymbol}  {items.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0).toFixed(2)}
                    </TableCell>
                    <TableCell sx={{ p: 0.1, py: 0.3, border: '1px solid #e5e7eb' }}></TableCell>
                    <TableCell sx={{ p: 0.1, py: 0.3, textAlign: 'right', fontSize: '0.875rem', fontWeight: 700, border: '1px solid #e5e7eb' }}>
                      {currencySymbol}  {items.reduce((sum, item) => sum + (parseFloat(item.discAmt) || 0), 0).toFixed(2)}
                    </TableCell>
                    <TableCell sx={{ p: 0.1, py: 0.3, border: '1px solid #e5e7eb' }}></TableCell>
                    <TableCell sx={{ p: 0.1, py: 0.3, textAlign: 'right', fontSize: '0.875rem', fontWeight: 700, border: '1px solid #e5e7eb' }}>
                      {currencySymbol}  {items.reduce((sum, item) => sum + (parseFloat(item.taxable) || 0), 0).toFixed(2)}
                    </TableCell>
                    <TableCell sx={{ p: 0.1, py: 0.3, textAlign: 'right', fontSize: '0.875rem', fontWeight: 700, border: '1px solid #e5e7eb' }}>
                      {currencySymbol}  {items.reduce((sum, item) => sum + ((parseFloat(item.vatAmount) || 0) + (parseFloat(item.igst) || 0)), 0).toFixed(2)}
                    </TableCell>
                    <TableCell sx={{ p: 0.1, py: 0.3, textAlign: 'right', fontSize: '0.875rem', fontWeight: 700, color: '#1976d2', border: '1px solid #e5e7eb' }}>
                      {currencySymbol}  {items.reduce((sum, item) => sum + (parseFloat(item.total) || 0), 0).toFixed(2)}
                    </TableCell>
                    <TableCell sx={{ p: 0.1, py: 0.3, border: '1px solid #e5e7eb' }}></TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>

        <DialogActions sx={{ p: 1.5, borderTop: '1px solid #e0e0e0', bgcolor: '#f5f5f5' }}>
          <Button
            variant="outlined"
            onClick={() => setViewAllItemsModalOpen(false)}
            sx={{
              textTransform: 'none',
              fontSize: '0.875rem',
              borderColor: '#999',
              color: '#666',
              '&:hover': {
                borderColor: '#666',
                bgcolor: '#f5f5f5'
              }
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default SaleReturnModal;
